import React from "react";
import { X, Printer, Building2, Loader2, Send, FileDown, FileText, Minus, Plus, ZoomIn, ZoomOut, Copy } from "lucide-react";
import { toast } from "sonner";
import JsBarcode from "jsbarcode";
import qrcodeGenerator from "qrcode-generator";
import api from "./api";
import { printViaIframe, loadLogoDataUrl } from "./utils/pdfUtils";
import { PrintPreviewModal } from "./components/PrintPreviewModal";
import { TailorSheetButton } from "./components/TailorSheetButton";
import { browserPrintHtml } from "./utils/browserPrintHtml";
import zenLogo from "./assets/zen-logo.png";
import zenLogo3d from "./assets/zen-logo-3d.png";
import stortexLogo from "./assets/stortex-logo.png";
import { REMOTE_PRICES } from "./artbeyPricing";

// ── ZEN_PDF_FONT_FACE ─────────────────────────────────────────────────────────
// Tarayıcı için: browserPrintHtml.js bu sabiti /fonts/... URL'leriyle replace eder.
// Backend için: pdf_utils.py._html_to_pdf_bytes bu sabiti base64 gömülü @font-face ile replace eder.
// Kural: bu sabitin içeriğini asla değiştirme — değişiklikler her zaman
//        browserPrintHtml.js (tarayıcı) veya pdf_utils.py (backend) üzerinden yapılır.
export const ZEN_PDF_FONT_FACE = `/* ZEN_PDF_FONT_FACE_PLACEHOLDER */`;

export const QUOTE_BRANDS = {
  zen: { key: "zen", name: "ZEN Perde", fullName: "ZEN Shading Systems", logo: zenLogo, detailLogo: zenLogo3d, accent: "#E5322A" },
  stortex: { key: "stortex", name: "Stortex", fullName: "Stortex External Curtain Systems", logo: stortexLogo, detailLogo: stortexLogo, accent: "#1E293B" },
};

// ============ TR → EN CURTAIN TERMS DICTIONARY ============
const TR_EN_DICT = {
  // Ürün türleri
  "Stor Perde": "Roller Blind",
  "Zebra Perde": "Zebra Blind",
  "Plise Perde": "Pleated Blind",
  "Dikey Perde": "Vertical Blind",
  "Panel Perde": "Panel Blind",
  "Ahşap Jaluzi": "Wood Venetian",
  "Metal Jaluzi": "Aluminum Venetian",
  "Zip Perde": "Zip Screen",
  "Dış Cephe Zip": "External Zip Screen",
  "Sineklik": "Insect Screen",
  "Rulo Sineklik": "Roll-Up Insect Screen",
  "Pileli Sineklik": "Pleated Insect Screen",
  "Sürgülü Sineklik": "Sliding Insect Screen",
  "Perde Motoru": "Curtain Motor",
  "Motorlu Perde Borusu": "Motorized Tube",
  "Kumanda": "Remote Control",
  // Motor / hareket
  "Motorlu": "Motorized",
  "motorlu": "motorized",
  "MOTORLU": "MOTORIZED",
  "Manuel": "Manual",
  "manuel": "manual",
  "MANUEL": "MANUAL",
  "Motor": "Motor",
  "Zincir": "Chain",
  // Özellikler
  "Eğimli": "Inclined",
  "Kurdelali": "Corded",
  "Redüktörlü": "With Gear",
  "Çocuk Zigılı": "Child Safety",
  "Gece / Gündüz": "Day / Night",
  "Renkli Kasa": "Colored Cassette",
  "Kasa": "Cassette",
  // Bileşenler / detay etiketleri
  "Mekanizma": "Mechanism",
  "Alt Çıta": "Bottom Bar",
  "Yan Kanal": "Side Channel",
  "Etek": "Hem",
  "Profil": "Profile",
  "Redüktör": "Gear Motor",
  "Doluluk": "Openness",
  "Cam Arası": "Between Glass",
  "Aksesuar": "Accessory",
  "Montaj / İşçilik": "Installation / Labor",
  "İşçilik": "Labor",
  "Montaj": "Installation",
  "Manuel Kalem": "Manual Item",
  "Sistem": "System",
  "Oda": "Room",
  "Kasa Rengi": "Cassette Color",
  "Kumanda Tipi": "Control Type",
  "Logo Baskı": "Logo Print",
  "Bal Kodu": "Lot Code",
  "Bal Durumu": "Lot Status",
  "Montajlı Kiriş": "Installed Beam",
  "Ekstralar": "Extras",
  "Not": "Note",
  "ZIP": "ZIP",
  // Ölçü cetveli
  "ÜRÜN": "PRODUCT",
  "GENİŞLİK": "WIDTH",
  "UZUNLUK": "HEIGHT",
  "ADET": "QTY",
  "B.FİYAT": "UNIT PRICE",
  "TOPLAM": "TOTAL",
  // Nakliye notu
  "MONTAJ DAHİL": "INSTALLATION INCLUDED",
  "MONTAJ HARİÇ": "INSTALLATION EXCLUDED",
  // Ek ürün türleri
  "Dikey Zip Perde": "Vertical Zip Screen",
  "Dış Cephe Zip Perde": "External Zip Screen",
  "Ön Kumaş": "Front Fabric",
  "Arka Kumaş": "Back Fabric",
  "Transparan Kumaş": "Transparent Fabric",
  "Ekran Kumaş": "Screen Fabric",
  "Yarı Saydam Kumaş": "Semi-Transparent Fabric",
  "Blackout Kumaş": "Blackout Fabric",
  "Sensör": "Sensor",
  "Montaj Tipi": "Installation Type",
  "Dikey Perde Sistemleri": "Vertical Blind Systems",
  "Transparan": "Transparent",
  "Kumaş": "Fabric",
  "MOTORLU": "MOTORIZED",
  "MANUEL": "MANUAL",
};

/**
 * Tek bir terimi sözlükten çevirir. lang !== "en" ise orijinal metni döndürür.
 * Büyük-küçük harf duyarlılığı için hem tam eşleşme hem de case-insensitive arama yapar.
 */
function tt(text, lang) {
  if (lang !== "en" || !text) return text;
  if (TR_EN_DICT[text]) return TR_EN_DICT[text];
  const lower = text.toLowerCase();
  for (const [tr, en] of Object.entries(TR_EN_DICT)) {
    if (tr.toLowerCase() === lower) return en;
  }
  return text;
}

/**
 * Bir metin parçasında geçen Türkçe terimleri İngilizceye çevirir (substring replace).
 */
function translateStr(text, lang) {
  if (lang !== "en" || !text) return text;
  let out = text;
  for (const [tr, en] of Object.entries(TR_EN_DICT)) {
    out = out.split(tr).join(en);
  }
  return out;
}

// Şirketin sabit banka/adres/vergi bilgileri — marka (Zen/Stortex) sadece üst logo/isimde değişir
const COMPANY_FIXED = {
  legalName: "ZEN GÖLGELENDİRME SİSTEMLERİ SANAYİ TİCARET LİMİTED ŞİRKET",
  address: "CEVİZLİ MAHALLESİ BAĞDAT CADDESİ NO:490-492 MALTEPE/İSTANBUL",
  tel: "0850 302 70 88",
  cep: "0532 379 13 84",
  web: "www.zenperde.com.tr",
  ibanTl: "TR40 0006 4000 0011 1480 7693 76 (ŞİRKET) İŞ BANKASI",
  ibanEur: "TR86 0006 4000 0021 1483 0491 10 (ŞİRKET) İŞ BANKASI",
  taxOffice: "KÜÇÜKYALI VD. 9971849863 KÜÇÜKYALI",
};

// WhatsApp teklif mesajı: Ayarlar > WhatsApp Teklif Metni (category=whatsapp) ile düzenlenebilir
export const WHATSAPP_TPL_DEFAULT = `Merhaba {musteri_unvan}! 👋

Sizin için hazırladığımız *#{teklif_no}* nolu perde/gölgelendirme teklifini inceleme şansınız oldu mu? 🏡

Mekanik sistemlerde kumaş seçimi veya ölçü detayları bazen kafa karıştırıcı olabiliyor; kafanıza takılan en ufak bir detay varsa veya bir noktayı değiştirmek isterseniz buradayım, beraber netleştirebiliriz. 🤝

📎 Teklif PDF: {link}

{iban}
{imza}`;

export const WHATSAPP_FIELDS = [
  { key: "wa_iban_tl", label: "IBAN (TL)", def: `${COMPANY_FIXED.ibanTl}`, multiline: false },
  { key: "wa_iban_eur", label: "IBAN (EUR)", def: `${COMPANY_FIXED.ibanEur}`, multiline: false },
  { key: "wa_iban_usd", label: "IBAN (USD)", def: "", multiline: false },
  { key: "wa_validity", label: "Geçerlilik Metni", def: "{gun} gün", multiline: false },
  { key: "wa_signature", label: "İmza / Firma Bilgisi", def: `ZEN Gölgelendirme Sistemleri\n${COMPANY_FIXED.tel} · ${COMPANY_FIXED.web}`, multiline: true },
  { key: "wa_template", label: "Serbest Mesaj Şablonu", def: WHATSAPP_TPL_DEFAULT, multiline: true },
];

// Ayarlardan gelen değerleri + teklif verisini şablona basar
export function buildQuoteWhatsappText(quote, cfg, link, gender = "") {
  const brand = (QUOTE_BRANDS[quote.brand] || QUOTE_BRANDS.zen).name;
  const get = (k) => {
    const f = WHATSAPP_FIELDS.find((x) => x.key === k);
    const v = cfg?.[k];
    return v === undefined || v === null || v === "" ? f.def : v;
  };
  const days = quote.validDays || 15;
  const validity = get("wa_validity").replace(/\{gun\}/g, String(days));
  const ibanLines = [
    get("wa_iban_tl") ? `İBAN (TL): ${get("wa_iban_tl")}` : "",
    get("wa_iban_eur") ? `İBAN (EUR): ${get("wa_iban_eur")}` : "",
    get("wa_iban_usd") ? `İBAN (USD): ${get("wa_iban_usd")}` : "",
  ].filter(Boolean).join("\n");
  const g = gender || quote.customerGender || "";
  const unvan = g === "erkek" ? "Bey" : g === "kadın" ? "Hanım" : "";
  const musteriAdi = quote.customerName || "-";
  const musteriUnvan = unvan ? `${musteriAdi} ${unvan}` : musteriAdi;
  const map = {
    marka: brand,
    teklifNo: quote.quoteNo || "",
    teklif_no: quote.quoteNo || "",
    musteri: musteriAdi,
    musteri_adi: musteriAdi,
    musteri_unvan: musteriUnvan,
    unvan,
    tutar: fmtQ(quote.total, quote.currency),
    gecerlilik: validity,
    link: link || "",
    iban: ibanLines,
    imza: get("wa_signature"),
  };
  let out = get("wa_template").replace(/\{(\w+)\}/g, (m, k) => (k in map ? map[k] : m));
  if (!link) out = out.split("\n").filter((l) => !/(?:Teklif PDF|📎).*:\s*$/.test(l.trim())).join("\n");
  return out.replace(/\n{3,}/g, "\n\n").trim();
}

export function defaultQuoteNotes(isYurtDisi, hasMontaj = false) {
  return [
    `NAKLİYE/TRANSPORT: ${isYurtDisi ? "EXW" : hasMontaj ? "MONTAJ DAHİL" : "MONTAJ HARİÇ"}`,
    "GARANTİ/WARRANTY: 2 YIL - 2 YEAR",
    "ELEKTRİK BAĞLANTISI MÜŞTERİYE AİTTİR.",
    "ÖDEME/PAYMENT: IN ORDER/SİPARİŞTE %50 TESLİMDEN ÖNCE/DELIVERY %50",
    "İSTANBUL DIŞI TAMİR İÇİN SERVİS ÜCRETİ TALEP EDİLMEKTEDİR.",
    "VİNÇ GEREKTİREN YERLERDE VİNÇ ÜCRETİ MÜŞTERİYE AİTTİR.",
    "FİYATLARIMIZA KDV DAHİL DEĞİLDİR.",
  ].join("\n");
}

/* Items listesine bakarak NAKLİYE satırını dinamik olarak düzeltir.
   Yurt dışı (sadece "EXW" içeren satır) ise dokunmaz.            */
export function patchNotesNakliye(notesText, items) {
  if (!notesText) return notesText;
  const hasMontaj = (items || []).some(it => it.isMontaj);
  return notesText.split("\n").map(line => {
    if (!line.startsWith("NAKLİYE/TRANSPORT:")) return line;
    if (line.includes("EXW") && !line.includes("MONTAJ")) return line; // Yurt dışı → dokunma
    return `NAKLİYE/TRANSPORT: ${hasMontaj ? "MONTAJ DAHİL" : "MONTAJ HARİÇ"}`;
  }).join("\n");
}

// Notlar metnini İngilizce teklife çevirir: şablon "TR/EN: ..." formatındaysa "/" sonrası alınır,
// aksi halde satır olduğu gibi kalır (serbest not girildiyse aynen gösterilir).
function translateNotesToEnglish(notesText) {
  if (!notesText) return "";
  return notesText.split("\n").map((line) => {
    const slashIdx = line.indexOf("/");
    const colonIdx = line.indexOf(":");
    if (slashIdx > -1 && colonIdx > -1 && slashIdx < colonIdx) {
      return line.slice(slashIdx + 1); // "NAKLİYE/TRANSPORT: X" -> "TRANSPORT: X"
    }
    return line;
  }).join("\n");
}

const LABELS = {
  tr: {
    quoteNo: "Teklif No", date: "Tarih", valid: "Geçerli", days: "Gün", preparedBy: "Hazırlayan",
    buyer: "Alıcı", project: "Proje", col_product: "ÜRÜN", col_dir: "YÖN", col_ral: "RAL", col_fabric: "KUMAŞ",
    col_detail: "DETAY", col_width: "GENİŞLİK", col_height: "UZUNLUK", col_qty: "ADET", col_unit: "B.FİYAT", col_total: "TOPLAM",
    subtotal: "TOPLAM", grandTotal: "G.TOPLAM", notes: "AÇIKLAMA / DESCRIPTION", bank: "BANK INFORMATION", sign: "ONAY İMZASI / APPROVEMENT SIGNATURE",
  },
  en: {
    quoteNo: "Quote No", date: "Date", valid: "Valid", days: "Days", preparedBy: "Prepared By",
    buyer: "Buyer", project: "Project", col_product: "PRODUCT", col_dir: "DIRECTION", col_ral: "RAL", col_fabric: "FABRIC",
    col_detail: "DETAIL", col_width: "WIDTH", col_height: "HEIGHT", col_qty: "QTY", col_unit: "UNIT PRICE", col_total: "TOTAL",
    subtotal: "SUBTOTAL", grandTotal: "GRAND TOTAL", notes: "DESCRIPTION", bank: "BANK INFORMATION", sign: "APPROVAL SIGNATURE",
  },
};

const fmtQ = (n, cur = "TL") => `${Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur}`;

// Kalem tutarını teklif para birimine çevirir (PDF satırları için)
function convertForQuote(amount, fromCur, toCur, rates) {
  if (!amount || fromCur === toCur) return Number(amount || 0);
  const eurTl = Number(rates?.eurToTl || 38);
  const usdTl = Number(rates?.usdToTl || 34);
  // fromCur → TL
  const inTL = fromCur === "TL" ? Number(amount) : fromCur === "EUR" ? Number(amount) * eurTl : Number(amount) * usdTl;
  // TL → toCur
  if (toCur === "TL") return inTL;
  if (toCur === "EUR") return inTL / eurTl;
  if (toCur === "USD") return inTL / usdTl;
  return Number(amount);
}

// ============ FIRMA SEÇ MODAL ============
export function BrandSelectModal({ onSelect, onClose }) {
  return (
    <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="zen-panel w-full max-w-md p-6 zen-anim-up">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold flex items-center gap-2"><Building2 className="w-5 h-5 text-indigo-500" /> Firma Seç</h3>
          <button onClick={onClose} data-testid="brand-select-close-btn"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-xs text-slate-500 mb-4">Teklif hangi markanız adına hazırlanacak?</p>
        <div className="grid grid-cols-2 gap-3">
          {Object.values(QUOTE_BRANDS).map((b) => (
            <button
              key={b.key}
              data-testid={`brand-select-${b.key}`}
              onClick={() => onSelect(b.key)}
              className="zen-panel p-5 flex flex-col items-center gap-3 hover:ring-2 hover:ring-indigo-400 transition"
            >
              <div style={{ background: "white" }} className="rounded-lg p-3 w-full flex items-center justify-center">
                <img src={b.detailLogo || b.logo} alt={b.name} className="h-16 object-contain" style={{ filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.3)) drop-shadow(0 0 12px rgba(34,211,238,0.18))" }} />
              </div>
              <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">{b.name}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============ TEKLİF PDF/YAZDIRMA HTML ============

// Üründen özellik listesi üretir: Kurdela, Zıgıl, Kasa, Motor, Eğimli...
function buildItemFeaturesTxt(it, includeMotor = true) {
  const f = [];
  if (includeMotor && it.motor && it.motorLabel) f.push(`Motor: ${it.motorLabel}`);
  else if (includeMotor && it.motor) f.push("Motorlu");
  if (it.kurdela) f.push("Kurdelali");
  if (it.reduktorlu) f.push("Redüktörlü");
  if (it.egimli) f.push("Eğimli");
  if (it.kasaTipi && it.kasaTipi !== "Standart") f.push(`Kasa: ${it.kasaTipi}`);
  if (it.kasaRengi && it.kasaRengi !== "RAL 9010 Beyaz" && it.kasaRengi !== "Beyaz") f.push(`Kasa: ${it.kasaRengi}`);
  if (it.zigilSistem) f.push(`Zıgıl: ${it.zigilSistem}`);
  if (it.zincirTipi && !it.zincirTipi.toLowerCase().includes("standart")) f.push(it.zincirTipi);
  if (it.cocukZigil && it.cocukZigil !== "Hayır" && it.cocukZigil !== "") f.push("Çocuk Zigılı");
  if (it.dikeyTipi) f.push(it.dikeyTipi);
  if (it.rayTipi) f.push(it.rayTipi);
  if (it.slatSize) f.push(`${it.slatSize}mm`);
  if (it.detay) f.push(it.detay);
  return f.length ? f.join(" · ") : "—";
}

const escapeQuoteHTML = (value) => String(value ?? "")
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;").replace(/'/g, "&#039;");

// Teknik aralıkları ve fiyat eklerini temizle: "(251-300cm)", "· +300₺/MT" vb.
const cleanTechSuffix = (s) => (s || "")
  .replace(/\s*\(\d{2,4}-\d{2,4}cm\)/gi, "")
  .replace(/\s*·\s*[+]?[\d.,]+\s*[₺$€].*$/g, "")
  .replace(/\s*\([^)]*\)\s*$/g, "")
  .trim();

function createBarcodeDataUri(value) {
  try {
    if (typeof document === "undefined") return "";
    const canvas = document.createElement("canvas");
    JsBarcode(canvas, String(value || "ZEN-TEKLIF"), {
      format: "CODE128", displayValue: false, margin: 0, height: 34,
      width: 1.4, background: "#FFFFFF", lineColor: "#111317",
    });
    return canvas.toDataURL("image/png");
  } catch { return ""; }
}

function createQrDataUri(value) {
  try {
    if (typeof document === "undefined") return "";
    const qr = qrcodeGenerator(0, "M");
    qr.addData(String(value || "ZEN TEKLIF"));
    qr.make();
    const modules = qr.getModuleCount();
    const scale = 4;
    const canvas = document.createElement("canvas");
    canvas.width = canvas.height = (modules + 2) * scale;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#111317";
    for (let row = 0; row < modules; row += 1) {
      for (let col = 0; col < modules; col += 1) {
        if (qr.isDark(row, col)) ctx.fillRect((col + 1) * scale, (row + 1) * scale, scale, scale);
      }
    }
    return canvas.toDataURL("image/png");
  } catch { return ""; }
}

function buildDetailedItemText(it, lang = "tr") {
  const values = [];
  const skip = (v) => !v || v === "Standart" || v === "Yok" || v === "Seçilmedi" || v === "—" || v === "-" || v === "";
  const add = (label, value) => { if (!skip(value) && value !== false && value !== undefined && value !== null) values.push(`${tt(label, lang)}: ${translateStr(String(value), lang)}`); };
  const base = buildItemFeaturesTxt(it, false);
  if (base !== "—") values.push(translateStr(base, lang));
  add("Oda", it.roomName);
  add("Model", it.zipModeli);
  add("Montaj", it.mounting);
  add("Sistem", it.sistemTuru || it.system14);
  add("ZIP", it.zipCesidi);
  add("Mekanizma", it.mekanizma);
  add("Ön Kumaş", it.fabric2 ? it.fabric : null);  // çiftli sistemde ayrı göster
  add("Arka Kumaş", it.fabric2);
  add("Kasa", it.kasa || it.kasaTipi);
  add("Kasa Rengi", it.kasaRengiLabel || it.kasaRengi);
  add("Profil", it.profilRengi);
  add("Alt Çıta", it.altCita);
  add("Yan Kanal", it.yanKanal);
  add("Kumanda", it.controlLabel || it.control);
  add("Etek", it.etekModel || it.lazerEtek);
  add("Redüktör", it.reduktor);
  add("Bal Kodu", it.balKodu);
  add("Bal Durumu", it.balDurumu);
  add("Montajlı Kiriş", it.montajliKiris);
  add("Logo Baskı", it.logoBaski);
  add("Doluluk", it.dolu);
  add("Cam Arası", it.ciftCamArasi);
  add("Ekstralar", it.extrasLabel);
  add("Not", it.note);
  if (it.geceGunduz) values.push(lang === "en" ? "Day / Night" : "Gece / Gündüz");
  if (it.renkliKasa) values.push(lang === "en" ? "Colored Cassette" : "Renkli Kasa");
  if (it.isAccessory) values.unshift(lang === "en" ? "Accessory" : "Aksesuar");
  if (it.isMontaj) values.unshift(lang === "en" ? "Installation / Labor" : "Montaj / İşçilik");
  if (it.isManual) values.unshift(lang === "en" ? "Manual Item" : "Manuel Kalem");
  return values.length ? values.join(" · ") : "—";
}

// ── Fiyatlandırma modu (tek kaynak) ──────────────────────────────────────────
// Eski kayıtlarda `pricingMode` alanı yok. Bu yüzden mod, canlı
// product_groups.pricingMode değerlerini yansıtan slug setlerinden türetilir.
// Aksi halde m² ile fiyatlanan ürünler "adet" gibi, adet ile fiyatlanan
// ürünler "m²" gibi gösterilip birim fiyat şişiyordu (K2).
const M2_PRICED_SLUGS = new Set([
  "ahsap_jaluzi_25", "ahsap_jaluzi_50", "ciftli_sistem", "dikey", "dosemelik",
  "metal_jaluzi_16_25", "natty_plise", "plise14", "plise25", "rustik",
  "sadece_kumas", "silhouette", "stor", "tul_fon_perde", "zebra",
]);
const MT_PRICED_SLUGS = new Set(["dikey_ray", "stor_sistem"]);
const ADET_PRICED_SLUGS = new Set(["zip_aksesuar"]);
const MATRIX_PRICED_SLUGS = new Set([
  "bioklimatik", "cam_tavan", "giyotin_cam", "kasetli_tente", "mafsalli_tente",
  "pencere_tentesi", "pergola", "sineklik_dikey", "sineklik_duble_yatay",
  "surme_cam_sistemi", "surme_sineklik", "zip_art110_helio",
]);

function pricingModeOf(it) {
  const explicit = String(it?.pricingMode || "").toLowerCase();
  if (explicit) return explicit;
  const slug = String(it?.groupSlug || "").toLowerCase();
  if (M2_PRICED_SLUGS.has(slug)) return "m2";
  if (MT_PRICED_SLUGS.has(slug)) return "mt";
  if (ADET_PRICED_SLUGS.has(slug)) return "adet";
  if (MATRIX_PRICED_SLUGS.has(slug) || slug.startsWith("skyzip")) return "matrix";
  return "";
}

// MT birimleri: "mt"/"m"/"metre" yanında "mtül" de metre üzerinden fiyatlanır (L1).
const isMeterUnit = (unit) => ["mt", "m", "metre", "mtül", "mtul"].includes(String(unit || "").toLowerCase());

// Otomatik üretilen kumaş kodları (TF-<zamandamgası>) teklif/föyde GÖSTERİLMEZ —
// kullanıcı yalnız kumaş adını girer ("3002 SCREEN"); kod kimlik olarak saklanır
// ama baskıda yer kaplamaz (29.10.2026 kullanıcı raporu).
function cleanFabricCode(code) {
  const c = String(code || "").trim();
  return /^TF-\d{6,}$/i.test(c) ? "" : c;
}

function buildDetailedQuoteRow(it, index, quoteCur, rates, lang = "tr", showZone = false) {
  const service = it.isAccessory || it.isMontaj || it.isManual || it.isExtraRow || it.isMotorRow;
  // BÖLGE sütunu (A4 teklif föyü): sıra no yanında dar alanda bölge bilgisi
  const zoneCell = showZone
    ? `<td style="font-size:5.5px;color:#64748B;line-height:1.3;padding:1px 2px">${escapeQuoteHTML((it.roomName || "").trim() || "—")}</td>`
    : "";
  const m2 = it.computedM2 > 0
    ? Number(it.computedM2).toFixed(2)
    : (it.widthCm && it.heightCm ? ((Number(it.widthCm) / 100) * (Number(it.heightCm) / 100)).toFixed(2) : "—");
  const direction = [it.controlSide || it.yon, it.cableDir].filter(Boolean).join(" / ") || "—";
  const ral = it.ralCode || it.ahsapRenk || it.slatColorCode || "—";
  const fabricParts = [it.fabric || it.fabricBrand, cleanFabricCode(it.fabricCode)].filter(Boolean);
  const fabric = fabricParts.length ? fabricParts.join(" / ") : "—";
  const mode = pricingModeOf(it);
  const isMT = isMeterUnit(it.unit); // MT
  // MT kalemlerde miktar kesirli olabilir (2,28 mt). Yuvarlanırsa birim fiyat
  // 1539/2 = 769,50 gibi şişer (K6) — MT'de gerçek miktar kullanılır.
  const qtyNum = isMT ? Number(it.qty || 1) : Math.round(Number(it.qty || 1));
  // Matris (adet/paket) fiyatlı ürünlerde birim "m²" görünse de satış adet
  // üzerindendir: 96 parça Sky Zip "96 m²" değil "96 Ad" yazmalı.
  const isPiecePriced = mode === "matrix" || mode === "adet";
  const unitLabel = isPiecePriced ? "Ad" : (it.unit || "Ad");
  const qtyDisplay = isMT ? `${Number(it.qty || 1).toFixed(2)} mt` : `${qtyNum} ${unitLabel}`;
  const dispCur = quoteCur || it.currency;
  // Birim fiyat her zaman lineTotal/qty ile hesaplanır (unitPrice marj öncesi olabilir)
  let dispTotal = convertForQuote(it.lineTotal, it.currency, dispCur, rates);
  let dispUnit  = dispTotal / Math.max(0.0001, qtyNum);

  // Boyut bilgisini ürün hücresine ekle (tek kalem, birleştirilmemiş)
  const dimInfo = !service && it.widthCm && it.heightCm
    ? `<div style="font-size:6px;color:#888;margin-top:1px">${it.widthCm} × ${it.heightCm} cm</div>` : "";

  // Zip ürünleri için En/Boy/Montaj/Yön detay satırı
  const isZipItem = !service && it.groupSlug && (it.groupSlug === "zip_art110_helio" || it.groupSlug.startsWith("skyzip"));
  // m² kolonu: adet/matris fiyatlı ürünlerde yanıltıcı olduğu için gösterilmez
  const isMatrixItem = !service && (mode === "matrix" || mode === "adet" ||
    (it.groupSlug || "").toLowerCase().startsWith("sineklik"));
  const zipDimLine = isZipItem ? (() => {
    const parts = [];
    if (it.widthCm)  parts.push(`En: ${it.widthCm} cm`);
    if (it.heightCm) parts.push(`Boy: ${it.heightCm} cm`);
    if (it.mounting) parts.push(`Montaj: ${it.mounting}`);
    return parts.length ? `<div style="font-size:6px;color:#6366f1;margin-top:2px;font-weight:600">${parts.join(" · ")}</div>` : "";
  })() : "";

  // Motor alt satırı — 8 sütun
  const hasMotor = !service && it.motor && it.motorLabel && Number(it.motorPrice) > 0;
  let motorRow = "";
  if (hasMotor) {
    const moCur   = it.motorCurrency || it.currency;
    const moUnit  = convertForQuote(it.motorPrice, moCur, dispCur, rates);
    const moTotal = moUnit * qtyNum;
    dispUnit  = dispUnit  - moUnit;
    dispTotal = dispTotal - moTotal;
    motorRow = `<tr>
      <td class="c mono">&nbsp;</td>
      ${zoneCell}
      <td class="product-name" style="padding-left:14px">↳ ${escapeQuoteHTML(it.motorLabel)}</td>
      ${showZone ? "<td>&nbsp;</td>" : ""}
      <td>—</td>${showZone ? '<td class="c mono">—</td><td class="c mono">—</td>' : ""}<td class="c mono">—</td><td class="c mono">—</td>
      <td class="c mono">${qtyNum} Ad</td>
      <td class="r mono">${fmtQ(moUnit, dispCur)}</td>
      <td class="r mono strong">${fmtQ(moTotal, dispCur)}</td>
    </tr>`;
  }

  // Tül/Fon: birim fiyatı m² bazında göster (lineTotal / m² × qty)
  // Tek kaynak pricingModeOf: eski kayıtlarda pricingMode yoksa slug'dan türetilir (K2).
  // "adet" modu m² fiyatlı SAYILMAZ — aksi halde zip_aksesuar birim fiyatı şişer.
  const isM2Priced = !service && (
    it.isTulFon === true ||
    mode === "m2" ||
    (it.groupSlug || "").includes("tul_fon")
  );
  const isTulFon = isM2Priced; // geriye dönük uyumluluk
  const rawM2Single = it.widthCm && it.heightCm
    ? (Number(it.widthCm) / 100) * (Number(it.heightCm) / 100)
    : 0;
  // Faturalanan alan computedM2'den gelir: 10 cm yukarı yuvarlanmış + min m² uygulanmış değer.
  // Ham en×boy kullanılırsa 192×340 için 6,53 (doğrusu 6,80), 119,5×340 için 4,06 (doğrusu 4,08)
  // yazılıyordu — fiyat yuvarlanmış alandan, m² kolonu ham alandan hesaplanıyordu.
  const m2Single = Number(it.computedM2) > 0 ? Number(it.computedM2) : rawM2Single;
  const totalM2 = m2Single * Math.max(1, qtyNum);
  if (isM2Priced && totalM2 > 0) {
    dispUnit = dispTotal / totalM2;  // EUR/m²
  }
  // m² kolonu: adet fiyatlıda "—", m² fiyatlıda TOPLAM alan (tek parça alanı değil)
  const m2Display = isMatrixItem
    ? "—"
    : (isM2Priced && totalM2 > 0 ? totalM2.toFixed(2) : m2);

  // MİKTAR: m² ürünler için gerçek (yuvarlanmış) alan, MT için metre, diğerleri için adet
  const finalQtyDisplay = (isM2Priced && totalM2 > 0)
    ? `${totalM2.toFixed(2)} m²`
    : qtyDisplay;

  const isAcc = it.isAccessory || it.isMontaj;

  // ── Sub-kalem isim normalizasyonu ──────────────────────────────────────────

  let displayProduct = translateStr(it.product || "—", lang);
  let displayDetailOverride = null;

  if (it.isMotorRow) {
    // Motor standalone satırı
    displayDetailOverride = cleanTechSuffix(it.product);
    displayProduct = tt("Perde Motoru", lang);
  } else if (it.groupSlug?.endsWith("_control") || /^kumanda\s*[-–]/i.test(it.product || "")) {
    // Uzaktan kumanda
    const model = (it.product || "").replace(/^kumanda\s*[-–]\s*/i, "").trim();
    displayDetailOverride = cleanTechSuffix(model);
    displayProduct = tt("Kumanda", lang);
  } else if (it.groupSlug?.endsWith("_sensor") || /^sensör\s*[-–]/i.test(it.product || "")) {
    // Hava sensörü (yağmur, güneş vb.)
    const model = (it.product || "").replace(/^sensör\s*[-–]\s*/i, "").trim();
    displayDetailOverride = cleanTechSuffix(model);
    displayProduct = tt("Sensör", lang);
  } else if (/^mekanizma\s*[—–-]/i.test(it.product || "")) {
    // Boru/Mekanizma
    const model = (it.product || "").replace(/^mekanizma\s*[—–-]\s*/i, "").trim();
    displayDetailOverride = cleanTechSuffix(model);
    displayProduct = tt("Motorlu Perde Borusu", lang);
  }

  const prodCell = isAcc
    ? `<span style="color:#aaa;font-size:7px;margin-right:3px">└</span>${escapeQuoteHTML(displayProduct)}`
    : `${escapeQuoteHTML(displayProduct)}${isZipItem ? zipDimLine : ""}`;

  const detailCell = displayDetailOverride !== null
    ? escapeQuoteHTML(displayDetailOverride)
    : escapeQuoteHTML(buildDetailedItemText(it, lang));

  const motorCellDetail = service ? "—" : (it.motor
    ? escapeQuoteHTML(it.motorLabel || tt("Motorlu", lang))
    : `<span style="font-weight:700;color:#333">${tt("MANUEL", lang)}</span>`);
  const fabricTranslated = translateStr(fabric, lang);
  // KUMAŞ sütunu: yalnız kumaş adı — renk/RAL artık kendi dar sütununda (29.10)
  const renkCell = service ? "—" : (fabricTranslated !== "—" ? fabricTranslated : "—");
  const wCell = (service || !it.widthCm) ? "—" : it.widthCm;
  const hCell = (service || !it.heightCm) ? "—" : it.heightCm;

  // 8(+2) sütun: #, [BÖLGE], ÜRÜN, [DETAY], KUMAŞ, [RAL], GENİŞLİK, YÜKSEKLİK, MİKTAR, B.FİYAT, TOPLAM
  // DİKKAT (30.10.2026 PDF hatası): hücreler ASLA boş bırakılmaz — boş <td>
  // xhtml2pdf'te (tables.py/pisaTagTD) sütun genişliğini th'deki "width:N%" yerine
  // yalnızca padding'e eşitler → DETAY sütunu 3pt'ye çöker, yazılar KUMAŞ'a taşar,
  // sütun 0 çok daralırsa PDF üretimi "negative availWidth" ile çöker.
  // Boş görünecek hücreler "&nbsp;" ile yazılır (tarayıcıda görünüm aynı kalır).
  return `<tr class="${service ? "service-row" : ""}">
    <td class="c mono">${String(index + 1).padStart(2, "0")}</td>
    ${zoneCell}
    <td class="product-name${isAcc ? " acc-indent" : ""}">${prodCell}</td>
    ${showZone ? `<td style="font-size:6px;color:#64748B;line-height:1.35;padding:1px 3px">${escapeQuoteHTML((it.note || "").trim()) || "&nbsp;"}</td>` : ""}
    <td>${escapeQuoteHTML(renkCell)}</td>
    ${showZone ? `<td style="font-size:5.5px;color:#64748B;line-height:1.3;padding:1px 2px">${escapeQuoteHTML(ral)}</td>` : ""}
    ${showZone ? `<td style="font-size:5.5px;color:#64748B;line-height:1.3;padding:1px 2px">${escapeQuoteHTML(direction)}</td>` : ""}
    <td class="c mono">${wCell}</td>
    <td class="c mono">${hCell}</td>
    <td class="c mono">${finalQtyDisplay}</td>
    <td class="r mono">${fmtQ(dispUnit, dispCur)}</td>
    <td class="r mono strong">${fmtQ(dispTotal, dispCur)}</td>
  </tr>${motorRow}`;
}

function renderTermsTable(notesText, lang) {
  const lines = String(notesText || "").split(/\n+/).map((line) => line.trim()).filter(Boolean);
  if (!lines.length) return "";
  const term = (line) => {
    const idx = line.indexOf(":");
    const label = idx > -1 ? line.slice(0, idx) : (lang === "en" ? "NOTE" : "NOT");
    const value = idx > -1 ? line.slice(idx + 1).trim() : line;
    return `<table class="term-item"><tr><td class="term-label">${escapeQuoteHTML(label)}</td><td>${escapeQuoteHTML(value)}</td></tr></table>`;
  };
  const rows = [];
  for (let i = 0; i < lines.length; i += 2) {
    rows.push(`<tr><td>${term(lines[i])}</td><td>${lines[i + 1] ? term(lines[i + 1]) : "&nbsp;"}</td></tr>`);
  }
  return `<table class="terms-grid">${rows.join("")}</table>`;
}

// F3: Aynı isimli kalemleri birleştir (Zip/Sineklik hariç) ─────────────────────
const PDF_GROUP_EXCLUDED = ["zip_art110_helio", "sineklik", "surme_sineklik"];
function groupSameNameItems(items) {
  const result = [];
  const seen = new Map(); // lowercase product name → result index
  for (const it of items) {
    const slug = (it.groupSlug || "").toLowerCase();
    const isExcluded = PDF_GROUP_EXCLUDED.some(s => slug.includes(s)) || slug.startsWith("skyzip");
    if (isExcluded) { result.push({ ...it }); continue; }
    const key = (it.product || "").trim().toLowerCase();
    if (!key) { result.push({ ...it }); continue; }
    if (seen.has(key)) {
      const idx = seen.get(key);
      const ex = result[idx];
      ex.qty = (Number(ex.qty) || 1) + (Number(it.qty) || 1);
      ex.lineTotal = (Number(ex.lineTotal) || 0) + (Number(it.lineTotal) || 0);
      ex.listLineTotal = (Number(ex.listLineTotal) || 0) + (Number(it.listLineTotal) || 0);
      if (it.computedM2 > 0) ex.computedM2 = (Number(ex.computedM2) || 0) + Number(it.computedM2);
      ex.unitPrice = ex.qty > 0 ? ex.lineTotal / ex.qty : ex.unitPrice;
      ex._mergedCount = (ex._mergedCount || 1) + 1;
    } else {
      seen.set(key, result.length);
      result.push({ ...it, _mergedCount: 1 });
    }
  }
  return result;
}

// Matris (adet başına) fiyatlanan ürünler: Sky Zip, Dikey Zip, sineklik vb.
// Fiyatı m² üzerinden DEĞİL, parça üzerinden hesaplanır; föy/A4'te m² kolonu
// toplam alan sanılıp adetle çarpılmamalıdır.
function isMatrixPricedItem(it) {
  const mode = pricingModeOf(it);
  if (mode) return mode === "matrix";
  const slug = (it.groupSlug || "").toLowerCase();
  return slug.startsWith("skyzip") || slug.startsWith("zip_art") ||
         slug.startsWith("sineklik") || slug.startsWith("surme_sineklik");
}

// F4: KUMAŞ bazlı gruplama — A4 Pro için ─────────────────────────────────────
function groupByFabric(items) {
  const mainResult   = [];  // perde / ana ürünler
  const accResult    = [];  // aksesuar / montaj / manuel
  const motorMap     = new Map(); // motorLabel → {label, qty, total, currency}
  const controlMap   = new Map(); // controlLabel → {label, qty, total, currency}
  const mainSeen     = new Map(); // fabric key → mainResult index
  const accSeen      = new Map(); // acc key    → accResult index

  for (const it of items) {
    const slug = (it.groupSlug || "").toLowerCase();
    const isExcluded      = PDF_GROUP_EXCLUDED.some(s => slug.includes(s)) || slug.startsWith("skyzip");
    const isMontajOrManual = it.isMontaj || it.isManual || it.isExtraRow;
    const isAcc            = it.isAccessory && !isMontajOrManual;
    const isMT             = isMeterUnit(it.unit);
    const productKey       = (it.product || "").trim().toLowerCase();
    const qty              = Number(it.qty) || 1;

    // Kumanda kalemlerini tüm ürün grupları için topla — sonunda tek bölüm olarak gösterilecek
    const isControlItem = slug.endsWith("_control") || /^kumanda\s*[-–]/i.test(it.product || "");
    if (isControlItem && isAcc) {
      const cKey = (it.product || "").trim().toLowerCase();
      // Fiyat 0 ise REMOTE_PRICES'tan otomatik bul
      let total = Number(it.lineTotal) || 0;
      if (total === 0 && it.product) {
        const lbl = (it.product || "").replace(/^kumanda\s*[-–]\s*/i, "").trim();
        for (const brand of Object.values(REMOTE_PRICES)) {
          const found = brand.find(r => r.label === lbl);
          if (found) { total = found.price * qty; break; }
        }
      }
      if (controlMap.has(cKey)) {
        const ex = controlMap.get(cKey);
        ex.qty   += qty;
        ex.total += total;
      } else {
        controlMap.set(cKey, {
          label:    it.product || "Kumanda",
          qty,
          total,
          currency: it.currency,
        });
      }
      continue;
    }

    // Motor bilgisini global map'e topla
    if (!isMontajOrManual && !isAcc && it.motor && Number(it.motorPrice) > 0) {
      const mKey = (it.motorLabel || "motor").trim().toLowerCase();
      if (motorMap.has(mKey)) {
        const mx = motorMap.get(mKey);
        mx.qty   += qty;
        mx.total += Number(it.motorPrice) * qty;
      } else {
        motorMap.set(mKey, {
          label:    it.motorLabel || "Motor",
          qty,
          total:    Number(it.motorPrice) * qty,
          currency: it.motorCurrency || it.currency,
        });
      }
    }

    // Montaj / manuel / excluded → direkt
    if (isExcluded || isMontajOrManual) { accResult.push({ ...it }); continue; }
    if (!productKey) { mainResult.push({ ...it }); continue; }

    // Aksesuar: aynı isim → miktar topla
    if (isAcc) {
      const ak = `acc|${productKey}`;
      if (accSeen.has(ak)) {
        const ex = accResult[accSeen.get(ak)];
        ex.qty         = (Number(ex.qty) || 0) + qty;
        ex.lineTotal   = (Number(ex.lineTotal) || 0) + (Number(it.lineTotal) || 0);
        ex.listLineTotal = (Number(ex.listLineTotal) || 0) + (Number(it.listLineTotal) || 0);
      } else {
        accSeen.set(ak, accResult.length);
        accResult.push({ ...it });
      }
      continue;
    }

    // MT bazlı: aynı isim → metre topla
    if (isMT) {
      const mk = `mt|${productKey}`;
      if (mainSeen.has(mk)) {
        const ex = mainResult[mainSeen.get(mk)];
        ex.qty         = (Number(ex.qty) || 0) + qty;
        ex.lineTotal   = (Number(ex.lineTotal) || 0) + (Number(it.lineTotal) || 0);
        ex.listLineTotal = (Number(ex.listLineTotal) || 0) + (Number(it.listLineTotal) || 0);
        ex._mergedCount = (ex._mergedCount || 1) + 1;
      } else {
        mainSeen.set(mk, mainResult.length);
        mainResult.push({ ...it, _mergedCount: 1, _isMTGroup: true });
      }
      continue;
    }

    // Ana ürün: kumaş + motor bazlı birleştir (Manuel ve Motorlu ayrı satır)
    const fabricKey = (it.fabric || it.ralCode || it.slatColorCode || "").trim().toLowerCase();
    const motorKey  = it.motor ? (it.motorLabel || "motorlu").trim().toLowerCase() : "manuel";
    const key       = `${productKey}|${fabricKey}|${motorKey}`;
    // m² fiyatlı ürünlerde alan ADET ile çarpılır (toplam fatura alanı).
    // Matris (adet) fiyatlı ürünlerde computedM2 tek parçanın alanı olarak kalır.
    const isMatrixItem = isMatrixPricedItem(it);
    const itemM2    = it.computedM2 > 0
      ? Number(it.computedM2) * (isMatrixItem ? 1 : qty)
      : (it.widthCm && it.heightCm ? (Number(it.widthCm) / 100) * (Number(it.heightCm) / 100) * (isMatrixItem ? 1 : qty) : 0);
    // Motor bedeli lineTotal'a gömülü olabilir. Ayrı motor satırı basıldığı için
    // ana satırdan DÜŞÜLECEK kısım burada hesaplanır — çift sayım olmaz (K3).
    // Öncelik: motorTotalInCur (kalem para biriminde, kur dönüşümü yapılmış toplam).
    const motorPortion = (!isMontajOrManual && !isAcc && it.motor)
      ? (Number(it.motorTotalInCur) > 0
        ? Number(it.motorTotalInCur)
        : (Number(it.motorPrice) > 0 ? Number(it.motorPrice) * qty : 0))
      : 0;

    if (mainSeen.has(key)) {
      const ex = mainResult[mainSeen.get(key)];
      ex.qty             = (Number(ex.qty) || 1) + qty;
      ex.lineTotal       = (Number(ex.lineTotal) || 0) + (Number(it.lineTotal) || 0);
      ex.listLineTotal   = (Number(ex.listLineTotal) || 0) + (Number(it.listLineTotal) || 0);
      ex.computedM2      = (Number(ex.computedM2) || 0) + itemM2;
      ex.motorTotalInCur = (Number(ex.motorTotalInCur) || 0) + motorPortion;
      ex._mergedCount    = (ex._mergedCount || 1) + 1;
      ex._mergedSizes    = ex._mergedSizes || [];
      ex._mergedSizes.push({ w: it.widthCm, h: it.heightCm, qty, motorLabel: it.motorLabel, isMotorlu: !!it.motor });
    } else {
      mainSeen.set(key, mainResult.length);
      mainResult.push({
        ...it,
        computedM2:    itemM2,
        motorTotalInCur: motorPortion,
        _mergedCount:  1,
        _mergedSizes:  [{ w: it.widthCm, h: it.heightCm, qty, motorLabel: it.motorLabel, isMotorlu: !!it.motor }],
        _isFabricGroup: true,
      });
    }
  }

  // m² birim fiyatı
  for (const row of mainResult) {
    if (row._isFabricGroup && row.computedM2 > 0) {
      row._unitM2Price = row.lineTotal / row.computedM2;
    }
  }

  // Motorları tek satır olarak motorMap'ten oluştur
  const motorRows = [...motorMap.values()].map(m => ({
    isMotorRow:  true,
    isAccessory: true,
    product:     m.label,
    qty:         m.qty,
    unit:        "Ad",
    lineTotal:   m.total,
    listLineTotal: m.total,
    unitPrice:   m.qty > 0 ? m.total / m.qty : 0,
    currency:    m.currency,
  }));

  // Kumandaları tek satır olarak controlMap'ten oluştur
  const controlRows = [...controlMap.values()].map(c => ({
    isAccessory: true,
    isControlRow: true,
    groupSlug:   "kumanda_grouped",
    product:     c.label,
    qty:         c.qty,
    unit:        "Ad",
    lineTotal:   c.total,
    listLineTotal: c.total,
    unitPrice:   c.qty > 0 ? c.total / c.qty : 0,
    currency:    c.currency,
  }));

  // Sıralama: ana ürünler → motorlar → kumandalar → aksesuar → kontrol kalemleri → montaj/işçilik (her zaman en sonda)
  const regularAccsAll = accResult.filter(it => !it.isMontaj && !it.isManual && !it.isExtraRow);
  const isControlAcc = (it) => {
    const s = (it.groupSlug || "").toLowerCase();
    return s.endsWith("_control") || /^kumanda[\s\-–]/i.test(it.product || "") || (it.product || "").toLowerCase().trim() === "kumanda";
  };
  const regularControl = regularAccsAll.filter(isControlAcc);
  const regularNonControl = regularAccsAll.filter(it => !isControlAcc(it));
  const serviceRows = accResult.filter(it =>  it.isMontaj ||  it.isManual ||  it.isExtraRow);
  return [...mainResult, ...motorRows, ...controlRows, ...regularNonControl, ...regularControl, ...serviceRows];
}

// Kumaş bazlı gruplu satır HTML'i ─────────────────────────────────────────────
function buildFabricGroupedRow(it, index, quoteCur, rates, lang = "tr") {
  const service = it.isMontaj || it.isManual || it.isExtraRow;
  if (service) return buildDetailedQuoteRow(it, index, quoteCur, rates, lang);

  // Aksesuar: buildDetailedQuoteRow ile render (artık birleştirilmiş qty ile gelir)
  if (it.isAccessory) return buildDetailedQuoteRow(it, index, quoteCur, rates, lang);

  const isMerged = it._isFabricGroup && (it._mergedCount || 1) > 1;
  const isMT = it._isMTGroup || isMeterUnit(it.unit);
  // Matrisli ürünlerde (Zip vb.) m² sütunu gösterilmez — mod slug'dan da türetilir (K2)
  const isMatrix = ["matrix", "adet"].includes(pricingModeOf(it));
  const m2Total  = isMatrix ? "—" : (it.computedM2 > 0 ? Number(it.computedM2).toFixed(2) : (isMT ? "—" : "—"));
  const direction = [it.controlSide || it.yon, it.cableDir].filter(Boolean).join(" / ") || "—";
  const ral = it.ralCode || it.ahsapRenk || it.slatColorCode || "—";
  const fabricParts = [it.fabric || it.fabricBrand, cleanFabricCode(it.fabricCode)].filter(Boolean);
  const fabric = fabricParts.length ? fabricParts.join(" / ") : "—";
  const dispCur = quoteCur || it.currency;

  // MT ürünü: qty = toplam metre
  const qtyDisplay = isMT
    ? `${Number(it.qty || 0).toFixed(2)} mt`
    : `${Number(it.qty || 1)} Ad`;

  // Boyut bilgisi (tek kalem, birleştirilmemiş)
  const dimInfo = !isMerged && !isMT && it.widthCm && it.heightCm
    ? `<div style="font-size:6px;color:#888;margin-top:1px">${it.widthCm} × ${it.heightCm} cm</div>` : "";

  // Motor fiyatı lineTotal'a gömülü — ana satırda düş, motor standalone satırda ayrıca görünüyor
  const motorDeducted = convertForQuote(Number(it.motorTotalInCur) || 0, it.currency, dispCur, rates);
  const mainLineTotal = convertForQuote(Number(it.lineTotal) || 0, it.currency, dispCur, rates) - motorDeducted;

  // Birim fiyat — HER ZAMAN lineTotal/qty olarak göster (unitPrice marj öncesi olabilir)
  let unitDisplay;
  if (isMT) {
    const totalQty = Number(it.qty || 1);
    unitDisplay = fmtQ(mainLineTotal / totalQty, dispCur) + `<span style="font-size:5.5px;color:#888"> /mt</span>`;
  } else if (isMerged && it.computedM2 > 0 && !isMatrix) {
    unitDisplay = fmtQ(mainLineTotal / it.computedM2, dispCur) + `<span style="font-size:5.5px;color:#888"> /m²</span>`;
  } else {
    // Matrisli veya tek kalem: birim fiyat = toplam / adet (her zaman tutarlı)
    const qtyN = Math.max(1, Number(it.qty || 1));
    unitDisplay = fmtQ(mainLineTotal / qtyN, dispCur);
  }

  const totalDisplay = fmtQ(mainLineTotal, dispCur);

  // Motor sub-satırı artık kullanılmıyor (standalone satır olarak geliyor)
  const motorRow = "";

  // 11 sütun: #, ÜRÜN(+boyut), YÖN, RAL, MOTOR, KUMAŞ, DETAY, m²/MT, MİK., B.FİYAT, TOPLAM
  const motorCellHtml = it.motor
    ? `<span style="font-weight:700">${escapeQuoteHTML(it.motorLabel || tt("Motorlu", lang))}</span>`
    : `<span style="font-weight:700;color:#333">${tt("MANUEL", lang)}</span>`;
  const motorBadge = it.motor
    ? ` <b style="font-size:5.5px;font-weight:800;color:#E5322A;letter-spacing:0.3px"> · ${tt("MOTORLU", lang)}</b>`
    : "";
  const fabricTranslated = translateStr(fabric, lang);
  const renkFG = fabricTranslated !== "—" ? fabricTranslated : ral;
  const wFG = isMerged ? `<span style="font-size:5.5px;color:#888">Çoklu</span>` : (it.widthCm ? it.widthCm : "—");
  const hFG = isMerged ? `<span style="font-size:5.5px;color:#888">—</span>` : (it.heightCm ? it.heightCm : "—");

  // 8 sütun: #, ÜRÜN, RENK, GENİŞLİK, YÜKSEKLİK, ADET, B.FİYAT, TOPLAM
  return `<tr>
    <td class="c mono">${String(index + 1).padStart(2, "0")}</td>
    <td class="product-name">${escapeQuoteHTML(translateStr(it.product || "—", lang))}${motorBadge}</td>
    <td>${escapeQuoteHTML(renkFG)}</td>
    <td class="c mono">${wFG}</td>
    <td class="c mono">${hFG}</td>
    <td class="c mono">${qtyDisplay}</td>
    <td class="r mono">${unitDisplay}</td>
    <td class="r mono strong">${totalDisplay}</td>
  </tr>${motorRow}`;
}

// ── HIZLI TEKLİF TOPLULAŞTIRMA (29.10.2026) ──────────────────────────────────
// Müşteriye verilen tekliflerde ÖLÇÜLER gizlenir: senin ölçtüğün ürünler
// (m² fiyatlı iç cephe) BÖLGE + ürün türü + kumaş koduna göre tek satırda
// TOPLAM m² olarak toplanır; EN/BOY yazılmaz. Matris fiyatlı ürünlerde (zip,
// sky, tente vb.) ölçüyü müşteri verdiği için satırlar AYNI KALIR. Motor/kumanda/
// zincir/dikiş gibi yan ürünler perdelerden sonra toplam adet/metraj olarak basılır.
function aggregateItemsForFast(items, cur, fx) {
  const groups = new Map();
  const sideGroups = new Map();
  const matrix = [];
  const m2Of = (it) => (Number(it.computedM2) > 0
    ? Number(it.computedM2) * Math.max(1, Number(it.qty || 1))
    : (Number(it.widthCm) > 0 && Number(it.heightCm) > 0
      ? (Number(it.widthCm) / 100) * (Number(it.heightCm) / 100) * Math.max(1, Number(it.qty || 1))
      : 0));

  for (const it of items) {
    const svc = it.isAccessory || it.isMontaj || it.isManual || it.isExtraRow || it.isMotorRow;
    // Motor bedeli kalemin içinde gömülüdür (motorTotalInCur) — AYRI satır olur
    const motorTl = !svc && Number(it.motorTotalInCur) > 0
      ? convertForQuote(Number(it.motorTotalInCur), it.currency, cur, fx) : 0;
    const netTl = convertForQuote(it.lineTotal, it.currency, cur, fx) - motorTl;

    if (svc) {
      const label = (it.product || "—").replace(/^kumanda\s*[-–]\s*/i, "Kumanda — ");
      const key = `S|${label}|${it.unit || "Ad"}|${it.kdvRate ?? 20}`;
      const g = sideGroups.get(key) || { product: label, qty: 0, net: 0, unit: it.unit || "Ad", kdvRate: it.kdvRate ?? 20 };
      g.qty += Number(it.qty || 1);
      g.net += netTl;
      sideGroups.set(key, g);
    } else if (pricingModeOf(it) === "matrix") {
      matrix.push(it); // ölçü müşteri verisi — satır satır, ölçüsüyle kalır
    } else {
      const zone = (it.roomName || "").trim();
      const fabric = (it.fabric || it.fabricBrand || "").trim();
      const fabricCode = cleanFabricCode(it.fabricCode);
      // GRUPLAMA: BÖLGE + ürün türü + kumaş kodu (kullanıcı kuralı — RAL/kasa
      // ayırıcı DEĞİLDİR)
      const fabricKey = (fabricCode || fabric).toLowerCase();
      const key = `G|${zone}|${it.product || "—"}|${fabricKey}|${it.kdvRate ?? 10}`;
      const g = groups.get(key) || {
        product: it.product || "—", roomName: zone, fabric, fabricCode, color: "",
        kdvRate: it.kdvRate ?? 10, m2: 0, net: 0, notes: [], kasalar: new Set(),
      };
      g.m2 += m2Of(it);
      g.net += netTl;
      if (!g.color) g.color = (it.ralCode || it.ahsapRenk || it.slatColorCode || "").trim();
      if (!g.fabric) g.fabric = fabric;
      if (!g.fabricCode) g.fabricCode = fabricCode;
      if (it.kasaTipi) g.kasalar.add(it.kasaTipi);
      const n = (it.note || "").trim();
      if (n && !g.notes.includes(n)) g.notes.push(n);
      groups.set(key, g);
    }
  }

  // Motorlar: motorLabel'a göre toplam adet + toplam tutar (ayrı satır)
  const motorGroups = new Map();
  for (const it of items) {
    const svc = it.isAccessory || it.isMontaj || it.isManual || it.isExtraRow || it.isMotorRow;
    const motorTl = !svc && Number(it.motorTotalInCur) > 0
      ? convertForQuote(Number(it.motorTotalInCur), it.currency, cur, fx) : 0;
    const motorAdet = !svc ? Number(it.motorQty || 1) * Math.max(1, Number(it.qty || 1)) : 0;
    const label = !svc && it.motor && it.motorLabel ? it.motorLabel : null;
    if (label && motorAdet > 0) {
      const g = motorGroups.get(label) || { product: `Motor — ${label}`, qty: 0, net: 0, unit: "Ad", kdvRate: 20 };
      g.qty += motorAdet;
      g.net += motorTl;
      motorGroups.set(label, g);
    }
  }

  // Satır sırası: perdeler (BÖLGE'ye göre) → matris ürünler (ölçüleriyle) → yan ürünler
  // NOT: qty=1 bırakılır; toplam m² computedM2'de tutulur (satır üreteci m²'yi
  // qty ile çarpar — çift çarpım hatası 29.10 düzeltildi).
  const main = [...groups.values()]
    .sort((a, b) => (a.roomName || "").localeCompare(b.roomName || "", "tr") || (a.product || "").localeCompare(b.product || "", "tr"))
    .map((g) => ({
      product: g.kasalar.size === 1 ? `${g.product} (${[...g.kasalar][0]})` : g.product,
      roomName: g.roomName,
      fabric: g.fabric,
      fabricCode: g.fabricCode,
      ralCode: g.color,
      note: g.notes.join(" · "),
      unit: "m²",
      qty: 1,
      computedM2: Math.round(g.m2 * 100) / 100,
      pricingMode: "m2",
      kdvRate: g.kdvRate,
      currency: cur,
      lineTotal: Math.round(g.net * 100) / 100,
      unitPrice: g.m2 > 0 ? Math.round((g.net / g.m2) * 100) / 100 : 0,
    }))
    .concat(matrix);

  const svc = [...motorGroups.values(), ...sideGroups.values()]
    .map((g) => ({
      product: g.product,
      isAccessory: true,
      unit: g.unit,
      qty: Math.round(g.qty * 100) / 100,
      kdvRate: g.kdvRate,
      currency: cur,
      lineTotal: Math.round(g.net * 100) / 100,
      unitPrice: g.qty > 0 ? Math.round((g.net / g.qty) * 100) / 100 : 0,
    }));
  return { main, svc };
}

export function buildTeklifPrintHTML(quote, lang = "tr", opts = {}) {
  const brand = QUOTE_BRANDS[quote.brand] || QUOTE_BRANDS.zen;
  const cur = quote.currency || "TL";
  const fx = quote.rates || {};
  const eurToTl = Number(fx.eurToTl || 0);
  const usdToTl = Number(fx.usdToTl || 0);
  const allItems = quote.items || [];
  let flatMain, flatSvc;
  if (opts.fast) {
    // HIZLI TEKLİF: ölçüleri gizli, toplulaştırılmış satırlar (BÖLGE + ürün + kumaş)
    const agg = aggregateItemsForFast(allItems, cur, fx);
    flatMain = agg.main;
    flatSvc = agg.svc;
  } else {
    // DETAYLI YOL (31.10 kullanıcı isteği): satırlar SEPET SIRASINDA basılır —
    // her ürünün ekstraları (aksesuar/motor/kumanda) O ürünün HEMEN ALTINDADIR,
    // sayfa sonuna yığılmaz. Eski flatMain/flatSvc ayrımı iki filtrenin birleşimi
    // olduğundan küme aynı kalır; yalnız sıra korunur.
    flatMain = allItems;
    flatSvc = [];
  }
  const rows = [...flatMain, ...flatSvc].map((it, i) => buildDetailedQuoteRow(it, i, cur, fx, lang, true)).join("");
  const olcuCetveli = ""; // Kaldırıldı — her ölçü artık ayrı satır
  const vatBreakdown = quote.vatExempt ? [] : (quote.vatBreakdown || []).slice().sort((a, b) => Number(a.rate) - Number(b.rate));
  const grandTotal = Number(quote.total || 0);
  const notesText = lang === "en"
    ? translateNotesToEnglish(patchNotesNakliye(quote.notes, quote.items))
    : patchNotesNakliye(quote.notes, quote.items);
  const customer = quote.customerInfo || quote.customer || {};
  const vd = Number(quote.validDays || 15);
  const created = new Date(quote.createdAt || Date.now());
  const validUntil = new Date(created); validUntil.setDate(validUntil.getDate() + vd);
  const locale = lang === "en" ? "en-GB" : "tr-TR";
  const dateFmt = (date) => date.toLocaleDateString(locale, { day: "2-digit", month: "2-digit", year: "numeric" });
  const revisionNo = Number(quote.revisionNo || 1);
  // BELGE NO: siparişten yazdırılıyorsa SİPARİŞ NO, teklifse teklif no — belge ile başlık çelişmesin
  const belgeNo = quote.orderNo || quote.quoteNo || "PREVIEW";
  const codeValue = `${brand.key.toUpperCase()}-${belgeNo}-R${String(revisionNo).padStart(2, "0")}`;
  const qrPreviewValue = quote.shareUrl || `ZEN ERP|QUOTE:${belgeNo}|REV:${revisionNo}`;
  const qrData = createQrDataUri(qrPreviewValue);
  const barcodeData = createBarcodeDataUri(codeValue);
  const curToTl = cur === "EUR" ? eurToTl : cur === "USD" ? usdToTl : 1;
  const tlEquivalent = curToTl > 0 ? grandTotal * curToTl : 0;
  const discountAmount = Number(quote.discountAmount || 0);
  const customerContact = [quote.customerPhone || customer.phone, quote.customerEmail || customer.email].filter(Boolean).join(" · ");
  const customerTax = quote.customerTaxNo || customer.taxNo || "";
  const customerAddress = quote.customerAddress || customer.address || "";
  const projectRef = quote.projectRef || quote.id || `Q-${quote.quoteNo || "PREVIEW"}`;
  const bank = quote.bankAccount;
  const bankHtml = bank
    ? `<b>${escapeQuoteHTML(bank.accountHolder || COMPANY_FIXED.legalName)}</b><br>${escapeQuoteHTML(bank.bankName || "")}${bank.branch ? ` · ${escapeQuoteHTML(bank.branch)} Şubesi` : ""}<br>IBAN: ${escapeQuoteHTML(bank.iban || "")}${bank.swiftCode ? `<br>SWIFT: ${escapeQuoteHTML(bank.swiftCode)}` : ""}<br>${COMPANY_FIXED.taxOffice}`
    : `<b>${COMPANY_FIXED.legalName}</b><br>İş Bankası · Küçükyalı<br><b>EUR</b> ${COMPANY_FIXED.ibanEur}<br><b>TL</b> ${COMPANY_FIXED.ibanTl}<br>${COMPANY_FIXED.taxOffice}`;
  const titleQuote = lang === "en" ? "QUOTATION" : "TEKLİF";
  const sectionProducts = lang === "en" ? "01 / PRODUCT & TECHNICAL DETAILS" : "01 / ÜRÜN VE TEKNİK DETAYLAR";
  const sectionTerms = lang === "en" ? "02 / TERMS & CONDITIONS" : "02 / AÇIKLAMA VE KOŞULLAR";
  return `<!DOCTYPE html><html lang="${lang}"><head>
<meta charset="UTF-8"><meta http-equiv="Content-Type" content="text/html; charset=UTF-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<meta name="color-scheme" content="light">
<title>${escapeQuoteHTML(brand.name)} ${titleQuote} ${escapeQuoteHTML(belgeNo)}</title>
<style>
${ZEN_PDF_FONT_FACE}
  @page { size: A4 portrait; margin: 3mm 3mm 6mm 3mm; }
  * { box-sizing: border-box; }
  html, body { background: #fff; color: #111317; color-scheme: light; }
  body { margin: 0; padding: 3mm; font-family: ZenSans, -apple-system, 'Helvetica Neue', Helvetica, Arial, sans-serif; font-size: 7.5px; line-height: 1.3; border: 1.5px solid #1a1a1a; display: flex; flex-direction: column; min-height: calc(297mm - 12mm); }
  table { border-collapse: collapse; }
  .mono { font-family: ZenMono, 'Courier New', Consolas, monospace; } .strong { font-weight: 700; } .c { text-align: center; } .r { text-align: right; }
  .box-title { color: #5a5f6a; font-size: 5.8px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px; margin-bottom: 1px; }
  /* HEADER */
  .header { width: 100%; border-bottom: 1px solid #c8cbd0; }
  .header td { border-right: 1px solid #c8cbd0; padding: 5px 7px; vertical-align: middle; }
  .header td:last-child { border-right: 0; }
  .detail-logo { width: 148px; height: auto; display: block; image-rendering: -webkit-optimize-contrast; image-rendering: crisp-edges; filter: contrast(1.05) brightness(1.01) drop-shadow(0 0 1px rgba(180,195,210,0.7)) drop-shadow(0 1px 4px rgba(90,105,125,0.22)); }
  .quote-kicker { color: ${brand.accent}; font-size: 6px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; }
  .quote-no { font-size: 17px; font-weight: 700; line-height: 1; margin: 2px 0; color: #111317; }
  .qr-code { width: 44px; height: 44px; }
  .barcode { width: 96px; height: 22px; }
  /* META + PARTY */
  .meta, .party, .summary, .terms-grid, .footer-grid { width: 100%; margin-top: 3px; }
  .meta td, .party td { border: 1px solid #c8cbd0; padding: 3px 5px; vertical-align: top; }
  .meta td { width: 20%; }
  .party td { width: 33.33%; }
  .party-name { font-size: 9.5px; font-weight: 700; margin: 1px 0 1px; color: #0d1117; }
  .party-sub { font-size: 6.5px; color: #3a3f4a; line-height: 1.4; }
  /* SECTIONS */
  .section-head { width: 100%; margin-top: 5px; border: 1px solid #c8cbd0; background: #f3f4f5; }
  .section-head td { padding: 3px 6px; font-size: 6.5px; font-weight: 700; }
  .section-head .en { color: ${brand.accent}; text-align: right; font-size: 6px; }
  /* PRODUCTS TABLE */
  .products { width: 100%; table-layout: fixed; }
  .products thead { display: table-header-group; }
  .products th, .products td { border: 1px solid #c8cbd0; padding: 3px 2px; vertical-align: top; overflow-wrap: break-word; }
  .products th { background: ${brand.accent}; color: #fff; font-size: 5.5px; font-weight: 700; text-transform: uppercase; text-align: center; }
  .products td { font-size: 6.5px; color: #111317; }
  .products .product-name { font-weight: 700; }
  .products .product-name.acc-indent { font-weight: 400; color: #555; padding-left: 12px; border-left: 2px solid #d1d5db; }
  .products .service-row td { background: #f7f8f9; color: #4a4f5a; }
  /* SUMMARY */
  .summary td { vertical-align: top; }
  .rates-cell { width: 60%; padding-right: 5px; }
  .totals-cell { width: 40%; }
  .rates, .totals { width: 100%; }
  .rates td, .totals td { border: 1px solid #c8cbd0; padding: 3px 5px; }
  .rates .label { color: #5a5f6a; font-size: 5.8px; font-weight: 700; width: 22%; }
  .totals .label { width: 43%; font-size: 6.5px; }
  .totals .value { width: 57%; text-align: right; font-family: ZenMono, 'Courier New', monospace; font-weight: 700; font-size: 6.5px; }
  .totals .grand td { background: #eef0f2; border-top: 2px solid ${brand.accent}; font-size: 8px; font-weight: 700; }
  .totals .grand .value { color: ${brand.accent}; }
  /* PUSH FOOTER — notlar daima sayfa altında */
  .push-footer { flex: 1; min-height: 10mm; }
  /* TERMS */
  .terms-grid td { width: 50%; padding: 0; vertical-align: top; }
  .term-item { width: 100%; }
  .term-item td { border: 1px solid #c8cbd0; padding: 3px 5px; font-size: 6.5px; color: #111317; }
  .term-item .term-label { width: 34%; color: #4f545c; font-size: 5.8px; font-weight: 700; text-transform: uppercase; }
  /* FOOTER / BANK */
  .footer-grid td { border: 1px solid #c8cbd0; padding: 5px 6px; vertical-align: top; }
  .footer-grid .bank-cell { width: 60%; font-size: 6.3px; line-height: 1.45; color: #111317; }
  .footer-grid .sign-cell { width: 40%; }
  .signature-space { height: 34px; border-bottom: 1px dashed #8a8f97; }
  .doc-footer { margin-top: 4px; border-top: 1px solid #cfd2d6; padding-top: 3px; color: #747981; font-size: 5.8px; text-align: right; }
  .avoid-break { page-break-inside: avoid; }
  .olcu-sep { margin-top: 14px; page-break-before: always; break-before: page; }
</style></head><body>
  <table class="header"><tr>
    <td style="width:38%;vertical-align:middle;padding:5px 8px;">
      <img class="detail-logo" src="${brand.logo}" alt="${escapeQuoteHTML(brand.name)}">
      <div style="font-size:6px;font-weight:700;letter-spacing:2.5px;color:#5a5f6a;text-transform:uppercase;margin-top:3px;font-family:ZenSans,-apple-system,'Helvetica Neue',Helvetica,Arial,sans-serif">SHADING SYSTEMS</div>
    </td>
    <td style="width:28%"><table style="width:100%"><tr><td style="border:0;padding:0;width:52px"><img data-zen-code="qr" class="qr-code" src="${qrData}" alt="QR"></td><td style="border:0;padding:0 0 0 4px"><div class="box-title">${lang === "en" ? "DIGITAL DOCUMENT" : "DİJİTAL TEKLİF"}</div><img data-zen-code="barcode" class="barcode" src="${barcodeData}" alt="Barcode"><div class="mono" style="font-size:5.5px;margin-top:1px">${escapeQuoteHTML(codeValue)}</div></td></tr></table></td>
    <td style="width:34%;text-align:right"><div class="quote-kicker">${titleQuote} / QUOTATION</div><div class="quote-no">#${escapeQuoteHTML(belgeNo)}</div><div class="mono" style="font-size:6.5px">REV ${String(revisionNo).padStart(2, "0")} · ${escapeQuoteHTML(cur)}</div></td>
  </tr></table>

  <div style="margin-top:3px;border:1px solid #c8cbd0;padding:3px 7px;font-size:6.8px;display:flex;gap:0;flex-wrap:nowrap;white-space:nowrap;overflow:hidden">
    <span style="margin-right:14px"><span style="color:#5a5f6a;font-weight:700;text-transform:uppercase;font-size:6px">${lang === "en" ? "DATE" : "TARİH"}:</span> <b class="mono">${dateFmt(created)}</b></span>
    <span style="margin-right:14px"><span style="color:#5a5f6a;font-weight:700;text-transform:uppercase;font-size:6px">${lang === "en" ? "VALIDITY" : "GEÇERLİLİK"}:</span> <b>${vd} ${lang === "en" ? "Days" : "Gün"}</b></span>
    <span style="margin-right:14px"><span style="color:#5a5f6a;font-weight:700;text-transform:uppercase;font-size:6px">${lang === "en" ? "VALID UNTIL" : "BİTİŞ TARİHİ"}:</span> <b class="mono">${dateFmt(validUntil)}</b></span>
    <span style="margin-right:14px"><span style="color:#5a5f6a;font-weight:700;text-transform:uppercase;font-size:6px">${lang === "en" ? "PREPARED BY" : "HAZIRLAYAN"}:</span> <b>${escapeQuoteHTML(quote.preparedBy || "—")}</b></span>
    <span><span style="color:#5a5f6a;font-weight:700;text-transform:uppercase;font-size:6px">${lang === "en" ? "PROJECT REF." : "PROJE REF."}:</span> <b class="mono">${escapeQuoteHTML(projectRef)}</b></span>
  </div>
  ${(quote.fabricKodu || quote.ralKodu) ? `<div style="margin-top:2px;border:1px solid #c8cbd0;padding:3px 7px;font-size:6.8px;display:flex;gap:14px">
    ${quote.fabricKodu ? `<span><span style="color:#5a5f6a;font-weight:700;text-transform:uppercase;font-size:6px">${lang === "en" ? "FABRIC CODE" : "KUMAŞ KODU"}:</span> <b class="mono">${escapeQuoteHTML(quote.fabricKodu)}</b></span>` : ""}
    ${quote.ralKodu ? `<span><span style="color:#5a5f6a;font-weight:700;text-transform:uppercase;font-size:6px">RAL ${lang === "en" ? "CODE" : "KODU"}:</span> <b class="mono">${escapeQuoteHTML(quote.ralKodu)}</b></span>` : ""}
  </div>` : ""}

  <table class="party" style="margin-top:3px"><tr>
    <td style="width:33%"><div class="box-title">${lang === "en" ? "SELLER / ISSUER" : "SATICI / KESİDE EDEN"}</div><div class="party-name" style="font-size:8px">ZEN Gölgelendirme Sistemleri<br>San. Tic. Ltd. Şti.</div><div class="party-sub">${escapeQuoteHTML(COMPANY_FIXED.address)}<br>${COMPANY_FIXED.tel} · ${COMPANY_FIXED.web}<br>${COMPANY_FIXED.taxOffice}</div></td>
    <td style="width:34%"><div class="box-title">${lang === "en" ? "BUYER / CUSTOMER" : "ALICI / MÜŞTERİ"}</div><div class="party-name">${escapeQuoteHTML(quote.customerName || customer.name || "—")}</div><div class="party-sub">${[customerTax ? `VN: ${customerTax}` : "", customerContact, customerAddress].filter(Boolean).join(" · ")}</div></td>
    <td style="width:33%"><div class="box-title">${lang === "en" ? "PROJECT / DELIVERY" : "PROJE / TESLİMAT"}</div><div class="party-name">${escapeQuoteHTML(quote.projectName || "—")}</div><div class="party-sub">${escapeQuoteHTML(quote.deliveryAddress || customerAddress || "")}</div></td>
  </tr></table>

  <table class="section-head"><tr><td>${sectionProducts}</td><td class="en">PRODUCT SPECIFICATION</td></tr></table>
  <table class="products"><thead><tr>
    <th style="width:2%">#</th><th style="width:9%">${lang === "en" ? "ZONE" : "BÖLGE"}</th><th style="width:12%">${lang === "en" ? "PRODUCT" : "ÜRÜN"}</th><th style="width:22%">${lang === "en" ? "NOTES" : "DETAY"}</th><th style="width:9%">${lang === "en" ? "FABRIC" : "KUMAŞ"}</th><th style="width:5%">RAL</th><th style="width:3%">${lang === "en" ? "DIR" : "YÖN"}</th><th style="width:5%">${lang === "en" ? "W (cm)" : "EN (cm)"}</th><th style="width:5%">${lang === "en" ? "H (cm)" : "BOY (cm)"}</th><th style="width:6%">${lang === "en" ? "QTY / m²" : "MİKTAR"}</th><th style="width:11%">${lang === "en" ? "UNIT PRICE" : "B.FİYAT"}</th><th style="width:11%">${lang === "en" ? "TOTAL" : "TOPLAM"}</th>
  </tr></thead><tbody>${rows}</tbody></table>

  <table class="summary avoid-break"><tr><td class="rates-cell"><table class="rates">
    <tr><td class="label">${lang === "en" ? "CURRENCY" : "PARA BİRİMİ"}</td><td class="mono strong">${escapeQuoteHTML(cur)}</td><td class="label">EUR/TL</td><td class="mono strong">${eurToTl ? eurToTl.toLocaleString("tr-TR", { minimumFractionDigits: 4, maximumFractionDigits: 4 }) : "—"}</td></tr>
    <tr><td class="label">${lang === "en" ? "VAT STATUS" : "KDV DURUMU"}</td><td>${lang === "en" ? "Excluded" : "Hariç"}</td><td class="label">${cur !== "TL" ? (lang === "en" ? "TL EQUIV." : "TL KARŞILIĞI") : (lang === "en" ? "EUR EQUIV." : "EUR KARŞILIĞI")}</td><td class="mono strong">${cur !== "TL" ? (tlEquivalent ? fmtQ(tlEquivalent, "TL") : "—") : (eurToTl ? fmtQ(grandTotal / eurToTl, "EUR") : "—")}</td></tr>
    <tr><td class="label">USD/TL</td><td class="mono strong">${usdToTl ? usdToTl.toLocaleString("tr-TR", { minimumFractionDigits: 4, maximumFractionDigits: 4 }) : "—"}</td><td class="label">${lang === "en" ? "RATE DATE" : "KUR TARİHİ"}</td><td class="mono">${escapeQuoteHTML(fx.tcmbDate || dateFmt(created))}</td></tr>
  </table></td><td class="totals-cell"><table class="totals">
    <tr><td class="label">${lang === "en" ? "SUBTOTAL" : "ARA TOPLAM"}</td><td class="value">${fmtQ(quote.subtotal, cur)}</td></tr>
    <tr><td class="label">${lang === "en" ? "DISCOUNT" : "İSKONTO"}</td><td class="value">${fmtQ(discountAmount, cur)}</td></tr>
    ${quote.vatExempt
      ? `<tr><td class="label">${lang === "en" ? "VAT" : "KDV"}</td><td class="value" style="color:#10b981;font-weight:700">${lang === "en" ? "Exempt (0%)" : "İstisna (%0)"}</td></tr>`
      : vatBreakdown.map((group) => `<tr><td class="label">${lang === "en" ? "VAT" : "KDV"} %${escapeQuoteHTML(group.rate)}</td><td class="value">${fmtQ(group.kdv, cur)}</td></tr>`).join("")}
    <tr class="grand"><td class="label">${lang === "en" ? "GRAND TOTAL" : "GENEL TOPLAM"}</td><td class="value">${fmtQ(grandTotal, cur)}</td></tr>
  </table></td></tr></table>

  <div class="push-footer"></div>

  ${notesText ? `<table class="section-head"><tr><td>${sectionTerms}</td><td class="en">TERMS &amp; CONDITIONS</td></tr></table>${renderTermsTable(notesText, lang)}` : ""}

  ${cur === "TL" && eurToTl > 0 ? `<div style="margin-top:6px;padding:4px 8px;background:#F8F9FA;border:1px solid #c8cbd0;border-radius:3px;font-size:7.5px;color:#64748B;font-style:italic">
    ${lang === "en"
      ? `These prices are calculated based on the TCMB exchange rate${fx.tcmbDate ? " of " + fx.tcmbDate : ""}: 1 EUR = ${eurToTl.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TRY · 1 USD = ${usdToTl.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TRY.`
      : `Bu fiyatlar${fx.tcmbDate ? " " + fx.tcmbDate + " tarihli" : ""} TCMB kuru üzerinden hesaplanmıştır: 1 EUR = ${eurToTl.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL · 1 USD = ${usdToTl.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL.`}
  </div>` : ""}

  <table class="footer-grid avoid-break"><tr>
    <td class="bank-cell"><div class="box-title">${lang === "en" ? "BANK / TAX INFORMATION" : "BANKA / VERGİ BİLGİLERİ"}</div>${bankHtml}</td>
    <td class="sign-cell"><div class="box-title">${lang === "en" ? "BUYER APPROVAL" : "ALICI ONAYI"}</div><div class="signature-space"></div><div style="font-size:6px;margin-top:3px">${lang === "en" ? "STAMP / SIGNATURE" : "KAŞE / İMZA"}</div></td>
  </tr></table>

  <div class="doc-footer">${escapeQuoteHTML(brand.fullName)} · ${COMPANY_FIXED.web} · ${lang === "en" ? "PAGE" : "SAYFA"} <pdf:pagenumber> / <pdf:pagecount></div>

  ${olcuCetveli ? `<div class="olcu-sep">${olcuCetveli}</div>` : ""}
</body></html>`;
}

// ============ A5 HIZLI TEKLİF ŞABLONU ============
export function buildA5FastQuoteHTML(quote, lang = "tr") {
  const brand = QUOTE_BRANDS[quote.brand] || QUOTE_BRANDS.zen;
  const cur = quote.currency || "TL";
  const fx  = quote.rates || {};
  const eurToTl = Number(fx.eurToTl || 0);
  const usdToTl = Number(fx.usdToTl || 0);
  const allItems = quote.items || [];
  const mainItems = allItems.filter(x => !x.isAccessory && !x.isMontaj && !x.isManual);
  const accItems  = allItems.filter(x =>  x.isAccessory ||  x.isMontaj ||  x.isManual);
  const vatBreakdown = quote.vatExempt ? [] : (quote.vatBreakdown || []).slice().sort((a, b) => a.rate - b.rate);
  const grandTotal = quote.total || 0;
  const esc = (s) => String(s ?? "").replace(/</g, "&lt;");
  const vd  = quote.validDays || 15;
  const cd  = new Date(quote.createdAt || Date.now());
  const expDate = new Date(cd); expDate.setDate(expDate.getDate() + vd);
  const fmtDate = (d) => d.toLocaleDateString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" });
  const accent = brand.accent || "#E5322A";

  const itemRows = mainItems.map((it, i) => {
    const m2Val   = it.computedM2 > 0 ? Number(it.computedM2).toFixed(2) : (it.widthCm && it.heightCm ? ((it.widthCm / 100) * (it.heightCm / 100)).toFixed(2) : "—");
    const olcu    = (it.widthCm && it.heightCm) ? `${it.widthCm}×${it.heightCm}` : "—";
    const color   = it.ralCode || it.ahsapRenk || it.slatColorCode || "";
    const productLine = `${esc(translateStr(it.product || "—", lang))}${color ? ` <span style="color:#888;font-size:7px">(${esc(color)})</span>` : ""}`;
    const feats   = buildItemFeaturesTxt(it);
    const featLine   = feats !== "—" ? `<div style="font-size:7px;color:#999;margin-top:1px">${esc(translateStr(feats, lang))}</div>` : "";
    const fabric  = it.fabric || it.fabricBrand || "";
    const fabricLine = fabric ? `<div style="font-size:7.5px;color:#666;margin-top:1px;font-style:italic">${esc(translateStr(fabric, lang))}${it.controlSide ? ` · ${esc(it.controlSide)}` : ""}</div>` : (it.controlSide ? `<div style="font-size:7.5px;color:#666;margin-top:1px">${esc(it.controlSide)}</div>` : "");
    const motorLine = it.motor && it.motorLabel ? `<div style="font-size:7.5px;color:#888;margin-top:1px">${lang === "en" ? "Motor" : "Motor"}: ${esc(it.motorLabel)}</div>` : "";
    const isEven  = i % 2 === 1;
    // Kalem tutarlarını teklif para birimine çevir
    const rawTotal = convertForQuote(it.lineTotal, it.currency, cur, fx);
    const dispUnit  = rawTotal / Math.max(1, Number(it.qty || 1));
    const dispTotal = rawTotal;
    return `<tr style="${isEven ? "background:#f8f8fb" : ""}">
      <td class="c num">${i + 1}</td>
      <td class="prod">${productLine}${fabricLine}${motorLine}${featLine}</td>
      <td class="c">${olcu}</td>
      <td class="c">${m2Val}</td>
      <td class="c">${it.qty || 1}</td>
      <td class="r">${fmtQ(dispUnit, cur)}</td>
      <td class="r bold">${fmtQ(dispTotal, cur)}</td>
    </tr>`;
  }).join("");

  // Aksesuar/Hizmet kalemleri (örn. Dikiş) — her biri kendi metraj + fiyatıyla AYRI satır
  const accRows = accItems.map((it, i) => {
    const isMT = ["mt", "m", "metre"].includes((it.unit || "").toLowerCase());
    const qtyLabel = isMT ? `${Number(it.qty || 1).toFixed(2)} mt` : `${Math.round(Number(it.qty || 1))} ${it.unit || "Ad"}`;
    const rawAccTotal = convertForQuote(it.lineTotal, it.currency, cur, fx);
    const dispUnit  = rawAccTotal / Math.max(1, Number(it.qty || 1));
    const dispTotal = rawAccTotal;
    const isDikis = (it.groupSlug || "").endsWith("_dikis") || (it.product || "").startsWith("Dikiş —");
    const dikisBadge = isDikis ? `<span style="display:inline-block;margin-left:5px;padding:1px 6px;border-radius:8px;background:${accent}1A;color:${accent};font-size:6.5px;font-weight:800;letter-spacing:.02em;vertical-align:middle">✂ ${lang === "en" ? "SEWING" : "DİKİŞ"}</span>` : "";
    const productTranslated = translateStr(it.product || "—", lang);
    return `<tr style="background:#FAFAFC">
      <td class="c num">${mainItems.length + i + 1}</td>
      <td class="prod"><span style="color:#aaa;font-size:7px;margin-right:2px">└</span>${esc(productTranslated)}${dikisBadge}</td>
      <td class="c">—</td>
      <td class="c">—</td>
      <td class="c">${qtyLabel}</td>
      <td class="r">${fmtQ(dispUnit, cur)}</td>
      <td class="r bold">${fmtQ(dispTotal, cur)}</td>
    </tr>`;
  }).join("");
  const accSummary = "";

  const L = (tr, en) => lang === "en" ? en : tr;
  return `<!DOCTYPE html><html lang="${lang}"><head><meta charset="utf-8"><meta name="color-scheme" content="light">
<title>${lang === "en" ? "Quick Quote" : "Hızlı Teklif"} ${quote.quoteNo || ""}</title>
<style>
${ZEN_PDF_FONT_FACE}
  @page { size: A5 portrait; margin: 9mm 9mm 11mm 9mm; }
  * { box-sizing: border-box; }
  html, body { background: #fff; color-scheme: light; }
  body { font-family: ZenSans, -apple-system, 'Helvetica Neue', Helvetica, Arial, sans-serif; color: #1C1C1E; margin: 0; font-size: 9px; line-height: 1.45; }

  /* HEADER */
  .hdr { display: flex; justify-content: space-between; align-items: flex-end; padding-bottom: 7px; margin-bottom: 8px; border-bottom: 2.5px solid ${accent}; }
  .hdr img { height: 36px; object-fit: contain; }
  .hdr-right { text-align: right; }
  .hdr-right .doc-title { font-size: 16px; font-weight: 900; color: ${accent}; letter-spacing: .05em; line-height: 1; }
  .hdr-right .doc-sub  { font-size: 7.5px; color: #888; margin-top: 1px; }

  /* META GRID */
  .meta { display: grid; grid-template-columns: repeat(auto-fill, minmax(72px, 1fr)); gap: 1px; background: #E4E4EB; border: 1px solid #E4E4EB; border-radius: 5px; overflow: hidden; margin-bottom: 9px; }
  .meta-cell { background: #fff; padding: 4px 6px; }
  .meta-cell .lbl { font-size: 6.5px; text-transform: uppercase; color: #999; font-weight: 600; letter-spacing: .04em; display: block; }
  .meta-cell .val { font-size: 8.5px; font-weight: 700; color: #111; display: block; margin-top: 1px; }

  /* TABLE */
  table.tbl { width: 100%; border-collapse: collapse; font-size: 8.5px; }
  table.tbl thead tr { background: ${accent}; }
  table.tbl th { color: #fff; padding: 4px 4px; font-weight: 700; font-size: 7px; text-transform: uppercase; letter-spacing: .05em; }
  table.tbl td { border-bottom: 1px solid #EBEBF0; padding: 4px 4px; vertical-align: middle; }
  table.tbl tbody tr:last-child td { border-bottom: none; }
  .num  { text-align: center; color: #999; font-size: 7.5px; width: 5%; }
  .prod { width: 43%; text-align: left; }
  .c    { text-align: center; }
  .r    { text-align: right; }
  .bold { font-weight: 700; }

  /* TOTALS */
  .tot-wrap { display: flex; justify-content: flex-end; margin-top: 9px; }
  .tot-box { width: 52%; border: 1px solid #E4E4EB; border-radius: 5px; overflow: hidden; font-size: 8.5px; }
  .tot-row { display: flex; justify-content: space-between; align-items: center; padding: 3px 9px; border-bottom: 1px solid #F0F0F5; }
  .tot-row:last-child { border-bottom: none; }
  .tot-row .lbl  { color: #777; }
  .tot-row .val  { font-weight: 700; }
  .tot-row.grand { background: ${accent}; }
  .tot-row.grand .lbl,
  .tot-row.grand .val { color: #fff; font-size: 10px; font-weight: 900; }

  /* NOTES */
  .note-box { margin-top: 7px; font-size: 7.5px; color: #555; background: #F7F7FA; border-left: 2px solid #C8C8D4; border-radius: 0 3px 3px 0; padding: 5px 8px; }

  /* FOOTER */
  .footer { margin-top: 9px; font-size: 7px; color: #999; border-top: 1px solid #E8E8EE; padding-top: 5px; display: flex; justify-content: space-between; align-items: flex-end; }
  .footer-sign { border-top: 1px solid #bbb; padding-top: 3px; font-size: 7px; color: #aaa; text-align: center; min-width: 90px; }
</style></head><body>

  <div class="hdr">
    <img src="${brand.logo}" alt="${brand.name}">
    <div class="hdr-right">
      <div class="doc-title">${lang === "en" ? "QUICK QUOTE" : "HIZLI TEKLİF"}</div>
      <div class="doc-sub">${esc(brand.fullName)} · ${COMPANY_FIXED.tel}</div>
    </div>
  </div>

  <div class="meta">
    <div class="meta-cell"><span class="lbl">${L("Teklif No","Quote No")}</span><span class="val">${esc(quote.quoteNo) || "—"}</span></div>
    <div class="meta-cell"><span class="lbl">${L("Tarih","Date")}</span><span class="val">${fmtDate(cd)}</span></div>
    <div class="meta-cell"><span class="lbl">${L("Geçerlilik","Valid Until")}</span><span class="val">${fmtDate(expDate)}</span></div>
    <div class="meta-cell"><span class="lbl">${L("Müşteri","Customer")}</span><span class="val">${esc(quote.customerName) || "—"}</span></div>
    ${quote.projectName ? `<div class="meta-cell"><span class="lbl">${L("Proje","Project")}</span><span class="val">${esc(quote.projectName)}</span></div>` : ""}
    ${quote.fabricKodu ? `<div class="meta-cell"><span class="lbl">${L("Kumaş Kodu","Fabric Code")}</span><span class="val">${esc(quote.fabricKodu)}</span></div>` : ""}
    ${quote.ralKodu ? `<div class="meta-cell"><span class="lbl">${L("RAL Kodu","RAL Code")}</span><span class="val">${esc(quote.ralKodu)}</span></div>` : ""}
  </div>

  <table class="tbl">
    <thead><tr>
      <th>#</th><th style="text-align:left">${L("Ürün / Özellik","Product / Detail")}</th>
      <th>${L("Ölçü (cm)","Size (cm)")}</th><th>m²</th><th>${L("Ad","Qty")}</th>
      <th>${L("B.Fiyat","Unit Price")}</th><th>${L("Toplam","Total")}</th>
    </tr></thead>
    <tbody>${itemRows}${accRows}</tbody>
  </table>
  ${accSummary}

  <div class="tot-wrap">
    <div class="tot-box">
      <div class="tot-row"><span class="lbl">${L("Ara Toplam","Subtotal")}</span><span class="val">${fmtQ(quote.subtotal, cur)}</span></div>
      ${quote.vatExempt
        ? `<div class="tot-row"><span class="lbl">${L("KDV","VAT")}</span><span class="val" style="color:#10b981;font-weight:700">${L("İstisna (%0)","Exempt (0%)")}</span></div>`
        : vatBreakdown.map(g => `<div class="tot-row"><span class="lbl">${L("KDV","VAT")} %${g.rate}</span><span class="val">${fmtQ(g.kdv, cur)}</span></div>`).join("")}
      <div class="tot-row grand"><span class="lbl">${L("GENEL TOPLAM","GRAND TOTAL")}</span><span class="val">${fmtQ(grandTotal, cur)}</span></div>
      ${cur !== "TL" && eurToTl > 0 ? `<div class="tot-row" style="font-size:7px;color:#888;border-top:1px dashed #ddd;margin-top:3px;padding-top:3px"><span class="lbl">${L("≈ TL Karşılığı","≈ TL Equiv.")}</span><span class="val">${fmtQ(convertForQuote(grandTotal, cur, "TL", fx), "TL")}</span></div>` : ""}
      ${cur === "TL" && eurToTl > 0 ? `<div class="tot-row" style="font-size:7px;color:#888;border-top:1px dashed #ddd;margin-top:3px;padding-top:3px"><span class="lbl">${L("≈ EUR Karşılığı","≈ EUR Equiv.")}</span><span class="val">${fmtQ(convertForQuote(grandTotal, cur, "EUR", fx), "EUR")}</span></div>` : ""}
    </div>
  </div>
  ${cur === "TL" && eurToTl > 0 ? `<div style="font-size:7px;color:#94A3B8;text-align:right;margin-top:4px;font-style:italic">${
    lang === "en"
      ? `Prices calculated at${fx.tcmbDate ? " " + fx.tcmbDate : ""} TCMB exchange rate: 1 EUR = ${Number(eurToTl).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL · 1 USD = ${Number(usdToTl).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL`
      : `Bu fiyatlar${fx.tcmbDate ? " " + fx.tcmbDate + " tarihli" : ""} TCMB kuru üzerinden hesaplanmıştır: 1 EUR = ${Number(eurToTl).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL · 1 USD = ${Number(usdToTl).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL`
  }</div>` : ""}

  ${quote.notes ? `<div class="note-box">${esc(patchNotesNakliye(quote.notes, quote.items)).replace(/\n/g, "<br>")}</div>` : ""}

  <div class="footer">
    <div>${COMPANY_FIXED.taxOffice} · www.${COMPANY_FIXED.web} · ${COMPANY_FIXED.tel}<br>${L("Geçerlilik","Valid Until")}: ${fmtDate(expDate)} (${vd} ${L("gün","days")})</div>
    <div class="footer-sign">${L("Kaşe / İmza","Stamp / Signature")}</div>
  </div>
</body></html>`;
}


// ============ BAYİ FÖY ŞABLONU (Nokta Matris Stili) ============
export function buildDealerFoyHTML(quote, lang = "tr") {
  const L = (tr, en) => lang === "en" ? en : tr;
  const brand = QUOTE_BRANDS[quote.brand] || QUOTE_BRANDS.zen;
  const cur = quote.currency || "TL";
  const fx = quote.rates || {};
  const eurToTl = Number(fx.eurToTl || 0);
  const usdToTl = Number(fx.usdToTl || 0);
  const allItems = quote.items || [];
  const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const now = new Date(quote.createdAt || Date.now());
  const dateFmt = (d) => d.toLocaleDateString("tr-TR", { day:"2-digit", month:"2-digit", year:"2-digit" }).replace(/\./g, ".");
  const timeFmt = (d) => d.toLocaleTimeString("tr-TR", { hour:"2-digit", minute:"2-digit" });
  const grandTotal = Number(quote.total || 0);
  const vatBreakdown = quote.vatExempt ? [] : (quote.vatBreakdown || []).slice().sort((a,b) => a.rate - b.rate);
  const fmt2 = (n) => Number(n||0).toLocaleString("tr-TR", { minimumFractionDigits:2, maximumFractionDigits:2 });
  const customerAddress = quote.customerAddress || (quote.customerInfo || {}).address || (quote.customer || {}).address || "";
  const curSymbol = cur; // PDF'lerde sembol yerine metin kodu (₺/€/$ font desteği riskini sıfırlar)

  let totalAdt = 0, totalM2 = 0, hasMiktar = false;

  // groupByFabric ile aynı ürünleri birleştir, montaj/işçilik en sona
  const groupedItems = groupByFabric(allItems);

  const rows = groupedItems.map(it => {
    const qty = Number(it.qty || 1);
    const service = it.isAccessory || it.isMontaj || it.isManual || it.isExtraRow || it.isMotorRow;
    const mode = pricingModeOf(it);
    const isMatrix = !service && (mode === "adet" || isMatrixPricedItem(it));
    const m2 = it.computedM2 > 0 ? Number(it.computedM2) : (it.widthCm && it.heightCm ? (Number(it.widthCm)/100)*(Number(it.heightCm)/100) * (isMatrix ? 1 : qty) : 0);
    const isTulFon = it.isTulFon === true || it.groupSlug === "tul_fon_perde";
    let unit, miktar;
    const isMTUnit = isMeterUnit(it.unit);
    if (isMTUnit) { unit = "MT"; miktar = qty; }
    else if (service) { unit = L("Adet","Pcs"); miktar = qty; }
    else if (isMatrix) { unit = L("Adet","Pcs"); miktar = qty; }
    else if (isTulFon && m2 > 0) { unit = "mtül"; miktar = m2; totalM2 += m2; hasMiktar = true; }
    else if (m2 > 0) { unit = "m²"; miktar = m2; totalM2 += m2; hasMiktar = true; }
    else { unit = L("Adet","Pcs"); miktar = qty; }
    if (!service) totalAdt += qty;

    // İsim normalizasyonu (Perde Motoru / Kumanda / Motorlu Perde Borusu)
    let baseName = it.product || "—";
    if (it.isMotorRow) {
      baseName = `Perde Motoru [${cleanTechSuffix(it.product)}]`;
    } else if (it.groupSlug?.endsWith("_control") || /^kumanda\s*[-–]/i.test(it.product || "")) {
      const model = (it.product || "").replace(/^kumanda\s*[-–]\s*/i, "").trim();
      baseName = `Kumanda [${cleanTechSuffix(model)}]`;
    } else if (/^mekanizma\s*[—–-]/i.test(it.product || "")) {
      const model = (it.product || "").replace(/^mekanizma\s*[—–-]\s*/i, "").trim();
      baseName = `Motorlu Perde Borusu [${cleanTechSuffix(model)}]`;
    } else {
      const extras = [it.fabric || it.fabricBrand, cleanFabricCode(it.fabricCode), it.ralCode].filter(Boolean);
      if (it.motor && it.motorLabel) extras.push(it.motorLabel);
      if (isTulFon && it.astarli && it.astarKumas) {
        const astarCode = cleanFabricCode(it.astarFabricCode) ? ` (${cleanFabricCode(it.astarFabricCode)})` : "";
        extras.push(`${L("Astar","Lining")}: ${it.astarKumas}${astarCode} - impex`);
      }
      baseName = it.product + (extras.length ? ` [${extras.join(" · ")}]` : "");
    }

    // Ayrı motor satırı basılıyor → motor bedeli ana satırdan düşülür (K3).
    // Böylece föydeki satır toplamları teklif/sipariş lineTotal toplamıyla birebir tutar.
    const motorPart = (!service && Number(it.motorTotalInCur) > 0)
      ? convertForQuote(Number(it.motorTotalInCur), it.currency, cur, fx)
      : 0;
    const lineTotal = convertForQuote(it.lineTotal, it.currency, cur, fx) - motorPart;
    // Miktar sütunu m²/mtül ise fiyat da o ölçüye göre verilir:
    // böylece "Miktar × Fiyat = Toplam" föyde doğrudan tutar.
    const measureUnit = unit === "m²" || unit === "mtül";
    const unitPrice = measureUnit && miktar > 0
      ? lineTotal / miktar
      : (qty > 0 ? lineTotal / qty : convertForQuote(it.unitPrice, it.currency, cur, fx));
    return `<tr>
      <td class="prod">${esc(baseName)}</td>
      <td class="num">${fmt2(qty)}</td>
      <td class="num">${miktar > 0 ? fmt2(miktar) : "—"}</td>
      <td class="ctr">${unit}</td>
      <td class="num">${fmt2(unitPrice)}</td>
      <td class="num">${fmt2(lineTotal)} ${curSymbol}</td>
    </tr>`;
  }).join("");

  const catLine = totalAdt > 0 ? `<tr class="cat">
    <td>${L("PERDE","CURTAIN")}</td>
    <td class="num">${fmt2(totalAdt)}</td>
    <td class="num">${hasMiktar ? fmt2(totalM2) : "—"}</td>
    <td colspan="3"></td>
  </tr>` : "";

  return `<!DOCTYPE html><html lang="${lang}"><head><meta charset="utf-8"><meta name="color-scheme" content="light">
<title>${L("Bayi Föy","Dealer Sheet")} ${esc(quote.quoteNo || "")}</title>
<style>
${ZEN_PDF_FONT_FACE}
  @page { size: A4 portrait; margin: 10mm 13mm; }
  * { box-sizing: border-box; }
  html, body { background:#fff; color:#000; color-scheme:light; }
  body { font-family:ZenMono,'Courier New', Courier, monospace; font-size:10.5px; line-height:1.55; margin:0; padding:0; }
  .row  { display:flex; justify-content:space-between; align-items:flex-start; gap:8px; }
  .row .r { text-align:right; white-space:nowrap; }
  hr   { border:none; border-top:1px solid #000; margin:5px 0; }
  .sevk { margin:5px 0; }
  table.tbl { width:100%; border-collapse:collapse; margin-top:2px; }
  table.tbl th { border-top:1px solid #000; border-bottom:1px solid #000; padding:3px 3px; font-size:9.5px; font-weight:bold; }
  table.tbl th.prod { text-align:left; }
  table.tbl th.num  { text-align:right; }
  table.tbl th.ctr  { text-align:center; }
  table.tbl td { padding:2px 3px; font-size:10px; vertical-align:top; }
  table.tbl td.prod { }
  table.tbl td.num  { text-align:right; white-space:nowrap; }
  table.tbl td.ctr  { text-align:center; }
  .cat td { border-top:1px solid #000; font-weight:bold; padding:3px 3px; }
  .totals { width:100%; border-collapse:collapse; margin-top:6px; }
  .totals td.lbl { text-align:right; padding:2px 6px; font-weight:bold; }
  .totals td.val { text-align:right; white-space:nowrap; padding:2px 0; font-weight:bold; min-width:130px; }
  .totals tr.grand td { border-top:1px solid #000; }
  .small { font-size:8.5px; color:#444; }
</style></head><body>

<div class="row">
  <span><b>${esc(brand.fullName.toUpperCase())}</b></span>
  <span class="r">${L("Föy No","Sheet No")} ${esc(quote.quoteNo || "ÖNIZLEME")} &nbsp;-0&nbsp; ${dateFmt(now)} &nbsp;${timeFmt(now)}</span>
</div>
<div class="row">
  <span>${COMPANY_FIXED.cep}</span>
  <span class="r">${usdToTl > 0 ? `$:${Number(usdToTl).toFixed(4)}&nbsp;&nbsp;` : ""}${eurToTl > 0 ? `€:${Number(eurToTl).toFixed(4)}` : ""}</span>
</div>
<div class="row" style="margin-top:3px">
  <span>${L("Açıklama:","Description:")}</span>
  <span class="r">&nbsp;&nbsp;......<b>${L("TEKLİFTİR","QUOTATION")}</b>......&nbsp;&nbsp;&nbsp;&nbsp;${L("Sip. Tar. :","Date:")} ${dateFmt(now)}</span>
</div>
<div class="row">
  <span>${L("Sipariş Veren:","Customer:")} ${esc(quote.customerName || "—")}</span>
  <span class="r">${L("Teslim/Talep Tar:","Delivery Date:")} —</span>
</div>
<div class="row">
  <span>${L("Hazırlayan:","Prepared by:")} ${esc(quote.preparedBy || "—")}</span>
</div>
<hr>
<div class="sevk">${L("SEVK:","SHIP TO:")}&nbsp;&nbsp;${esc(customerAddress || quote.customerName || "—")}</div>
<hr>

<table class="tbl">
  <thead><tr>
    <th class="prod" style="width:46%">${L("Açıklama","Description")}</th>
    <th class="num"  style="width:9%">${L("Adt","Qty")}</th>
    <th class="num"  style="width:10%">${L("Miktar","Amount")}</th>
    <th class="ctr"  style="width:8%">${L("Birim","Unit")}</th>
    <th class="num"  style="width:12%">${L("Fiyatı","Unit Price")}</th>
    <th class="num"  style="width:15%">${L("Toplam","Total")}</th>
  </tr></thead>
  <tbody>
    ${rows}
    ${catLine}
  </tbody>
</table>

<table class="totals">
  <tr><td class="lbl">${L("Ara Toplam :","Subtotal :")}</td><td class="val">${fmt2(quote.subtotal)} ${curSymbol}</td></tr>
  ${quote.vatExempt
    ? `<tr><td class="lbl">${L("KDV :","VAT :")}</td><td class="val" style="color:#10b981;font-weight:700">${L("İstisna (%0)","Exempt (0%)")}</td></tr>`
    : vatBreakdown.map(g => `<tr><td class="lbl">${L("KDV","VAT")} %${g.rate} :</td><td class="val">${fmt2(g.kdv)} ${curSymbol}</td></tr>`).join("")}
  <tr class="grand"><td class="lbl">${L("Genel Toplam :","Grand Total :")}</td><td class="val">${fmt2(grandTotal)} ${curSymbol}</td></tr>
</table>

<hr style="margin-top:8px">
<div class="small" style="text-align:center">${COMPANY_FIXED.address} &nbsp;·&nbsp; ${COMPANY_FIXED.tel} &nbsp;·&nbsp; ${COMPANY_FIXED.web}</div>
</body></html>`;
}


// ============ YAZDIRMA / PAYLAŞIM MODAL ============
export function QuotePrintModal({ quote, onClose }) {
  const [showTailorSheet, setShowTailorSheet] = React.useState(false);
  // TEK ÇATI: teklif yazdırma da siparişlerdeki merkezî yazdırma modalını kullanır
  const [showPrintCenter, setShowPrintCenter] = React.useState(false);
  const [lang, setLang] = React.useState("tr");
  const [tpl, setTpl] = React.useState("a4"); // "a4" | "a5" | "foy"
  const [bankAccount, setBankAccount] = React.useState(quote.bankAccount || null);
  const [customerInfo, setCustomerInfo] = React.useState(quote.customerInfo || null);
  const [rates, setRates] = React.useState(quote.rates || null);
  const [sharing, setSharing] = React.useState(false);
  const [copying, setCopying] = React.useState(false);
  const [waCfg, setWaCfg] = React.useState({});
  const [zoom, setZoom] = React.useState(1.0);

  React.useEffect(() => {
    if (quote.bankAccount) return;
    const bid = quote.bankAccountId;
    api.get("/bank-accounts").then(({ data }) => {
      const acc = bid ? (data.find(b => b.id === bid) || data.find(b => b.isDefault) || null)
                      : (data.find(b => b.isDefault) || null);
      setBankAccount(acc);
    }).catch(() => {});
  }, [quote.bankAccountId, quote.bankAccount]);

  React.useEffect(() => {
    if (!quote.customerId || quote.customerInfo) return;
    api.get("/customers")
      .then(({ data }) => setCustomerInfo((data || []).find((c) => c.id === quote.customerId) || null))
      .catch(() => {});
  }, [quote.customerId, quote.customerInfo]);

  React.useEffect(() => {
    if (quote.rates) return;
    api.get("/rates").then(({ data }) => setRates(data || null)).catch(() => {});
  }, [quote.rates]);

  React.useEffect(() => {
    api.get("/settings", { params: { category: "whatsapp" } })
      .then(({ data }) => setWaCfg(Object.fromEntries((data || []).map((r) => [r.key, r.value]))))
      .catch(() => {});
  }, []);

  const html = tpl === "a5"
    ? buildTeklifPrintHTML({ ...quote, bankAccount, customerInfo, rates }, lang, { fast: true })
    : tpl === "foy"
    ? buildDealerFoyHTML({ ...quote, bankAccount, customerInfo, rates }, lang)
    : buildTeklifPrintHTML({ ...quote, bankAccount, customerInfo, rates }, lang);

  const filename = `Teklif-${quote.quoteNo || "X"}.pdf`;
  const barcodeValue = `${String(quote.brand || "zen").toUpperCase()}-${quote.quoteNo || "X"}-R${String(quote.revisionNo || 1).padStart(2, "0")}`;

  const print = () => {
    printViaIframe(browserPrintHtml(html));
  };

  // Logo webpack URL'lerini base64 data URL ile degistirir (backend PDF icin)
  const patchHtmlForPdf = async (rawHtml) => {
    try {
      const logoDataUrl = await loadLogoDataUrl();
      if (!logoDataUrl) return rawHtml;
      const brand = QUOTE_BRANDS[quote.brand] || QUOTE_BRANDS.zen;
      return rawHtml
        .replace(brand.logo, logoDataUrl)
        .replace(brand.detailLogo, logoDataUrl);
    } catch { return rawHtml; }
  };

  const downloadPdf = async () => {
    setSharing(true);
    try {
      const pdfHtml = await patchHtmlForPdf(html);
      const { data } = await api.post("/share/html-pdf", { html: pdfHtml, filename, refType: "quote", refId: quote.id, barcodeValue });
      const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      window.open(`${baseUrl}${data.path}/file`, "_blank");
    } catch (e) {
      toast.error("PDF oluşturulamadı");
    } finally { setSharing(false); }
  };

  const shareWhatsapp = async () => {
    const phone = String(quote.customerPhone || customerInfo?.phone || "").replace(/[^0-9]/g, "");
    const intl = phone ? (phone.startsWith("90") ? phone : phone.replace(/^0+/, "90")) : "";
    setSharing(true);
    let link = "";
    try {
      const pdfHtml = await patchHtmlForPdf(html);
      const { data } = await api.post("/share/html-pdf", { html: pdfHtml, filename, refType: "quote", refId: quote.id, barcodeValue });
      const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      link = `${baseUrl}${data.path}`;
    } catch (e) {
      toast.error("PDF linki oluşturulamadı — metin olarak gönderiliyor");
    } finally { setSharing(false); }
    const msg = buildQuoteWhatsappText(quote, waCfg, link, customerInfo?.gender || "");
    const waUrl = intl
      ? `whatsapp://send?phone=${intl}&text=${encodeURIComponent(msg)}`
      : `whatsapp://send?text=${encodeURIComponent(msg)}`;
    window.open(waUrl, "_self");
    if (link) toast.success("WhatsApp açıldı — PDF indirme linki mesaja eklendi");
  };

  const copyLink = async () => {
    setCopying(true);
    try {
      const pdfHtml = await patchHtmlForPdf(html);
      const { data } = await api.post("/share/html-pdf", { html: pdfHtml, filename, refType: "quote", refId: quote.id, barcodeValue });
      const baseUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const shareUrl = `${baseUrl}${data.path}`;
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link Kopyalandı! Dilediğiniz uygulamadan yapıştırabilirsiniz.");
    } catch (e) {
      toast.error("Link oluşturulamadı");
    } finally { setCopying(false); }
  };

  const handleWheel = React.useCallback((e) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      setZoom(prev => Math.min(Math.max(prev + (e.deltaY > 0 ? -0.1 : 0.1), 0.4), 2.5));
    }
  }, []);

  const ZOOM_STEPS = [0.5, 0.75, 1.0, 1.25, 1.5, 2.0];

  return (
    <div className="fixed inset-0 z-[70] bg-black/75 backdrop-blur-[2px] flex items-center justify-center p-2" data-testid="quote-print-modal">
      <div className="flex flex-col bg-white dark:bg-[#0C1220] rounded-2xl shadow-2xl overflow-hidden zen-anim-up"
           style={{ width: "calc(100vw - 24px)", height: "calc(100vh - 24px)", maxWidth: 1400 }}>

        {/* ── HEADER BAR ── */}
        <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200 dark:border-[#22304A] bg-slate-50 dark:bg-[#080D18] shrink-0 gap-3 flex-wrap">

          {/* Sol: Başlık + Şablon + Dil */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-black text-slate-700 dark:text-slate-200 mr-1">
              #{quote.quoteNo} {quote.revisionNo > 1 ? `Rev${quote.revisionNo}` : ""}
            </span>

            {/* Şablon toggle */}
            <div className="flex rounded-lg overflow-hidden border border-slate-200 dark:border-[#22304A]" data-testid="quote-tpl-toggle">
              {[
                { id: "a4",  icon: <FileText className="w-3 h-3" />,  label: "A4 Pro"    },
                { id: "a5",  icon: <FileDown className="w-3 h-3" />,  label: "A5 Hızlı"  },
                { id: "foy", icon: <Printer  className="w-3 h-3" />,  label: "Bayi Föy"  },
              ].map(({ id, icon, label }) => (
                <button key={id} onClick={() => setTpl(id)} data-testid={`quote-tpl-${id}-btn`}
                  className={`flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold transition-colors ${tpl === id ? "bg-indigo-600 text-white" : "bg-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-300"}`}>
                  {icon}{label}
                </button>
              ))}
            </div>

            {/* Dil toggle */}
            <TailorSheetButton record={quote} testId="quote-preview-tailor-sheet" onClick={() => setShowTailorSheet(true)} />
            <div className="flex rounded-lg overflow-hidden border border-slate-200 dark:border-[#22304A]" data-testid="quote-lang-toggle">
              {["tr", "en"].map(l => (
                <button key={l} onClick={() => setLang(l)} data-testid={`quote-lang-${l}-btn`}
                  className={`px-2.5 py-1 text-[11px] font-bold uppercase transition-colors ${lang === l ? "bg-[#E5322A] text-white" : "bg-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-300"}`}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          {/* Sağ: Zoom + Aksiyonlar + Kapat */}
          <div className="flex items-center gap-2">

            {/* Zoom */}
            <div className="flex items-center gap-0.5 bg-slate-100 dark:bg-[#131B2E] rounded-lg border border-slate-200 dark:border-[#22304A] px-1.5 py-1">
              <button onClick={() => setZoom(z => Math.max(+(z - 0.1).toFixed(1), 0.4))}
                className="p-0.5 rounded hover:bg-slate-200 dark:hover:bg-[#22304A] text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 transition">
                <Minus className="w-3 h-3" />
              </button>
              <select
                value={ZOOM_STEPS.reduce((acc, s) => Math.abs(s - zoom) < Math.abs(acc - zoom) ? s : acc, ZOOM_STEPS[2])}
                onChange={e => setZoom(Number(e.target.value))}
                className="text-[11px] font-mono bg-transparent text-slate-700 dark:text-slate-200 border-none outline-none cursor-pointer px-0.5 w-14 text-center">
                {ZOOM_STEPS.map(s => <option key={s} value={s}>{`${Math.round(s * 100)}%`}</option>)}
              </select>
              <button onClick={() => setZoom(z => Math.min(+(z + 0.1).toFixed(1), 2.5))}
                className="p-0.5 rounded hover:bg-slate-200 dark:hover:bg-[#22304A] text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 transition">
                <Plus className="w-3 h-3" />
              </button>
            </div>

            {/* Linki Kopyala */}
            <button onClick={copyLink} disabled={copying} data-testid="quote-print-copy-link-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-500 hover:bg-violet-600 active:bg-violet-700 text-white text-xs font-semibold transition disabled:opacity-50 shadow-sm">
              {copying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Copy className="w-3.5 h-3.5" />}
              Linki Kopyala
            </button>

            {/* WhatsApp */}
            <button onClick={shareWhatsapp} disabled={sharing} data-testid="quote-print-whatsapp-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 text-white text-xs font-semibold transition disabled:opacity-50 shadow-sm">
              {sharing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
              WhatsApp
            </button>

            {/* PDF İndir */}
            <button onClick={downloadPdf} disabled={sharing} data-testid="quote-pdf-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-sky-500 hover:bg-sky-600 active:bg-sky-700 text-white text-xs font-semibold transition disabled:opacity-50 shadow-sm">
              {sharing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
              PDF İndir
            </button>

            {/* Yazdır — TEK ÇATI: tüm evrak türleri yan yana (teklif/imâlat/terzi föyü/toptancı/etiket) */}
            <button onClick={() => setShowPrintCenter(true)} data-testid="quote-print-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-600 hover:bg-slate-700 text-white text-xs font-semibold transition shadow-sm">
              <Printer className="w-3.5 h-3.5" />
              Yazdır
            </button>

            <div className="w-px h-6 bg-slate-200 dark:bg-[#22304A]" />

            {/* Kapat */}
            <button onClick={onClose} data-testid="quote-print-close-btn"
              className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-[#22304A] text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition">
              <X className="w-4.5 h-4.5" />
            </button>
          </div>
        </div>

        {/* ── PDF ALAN — Zoom + Scroll ── */}
        <div
          className="flex-1 overflow-auto bg-slate-300 dark:bg-[#06090F] flex justify-center"
          style={{ padding: "20px 16px" }}
          onWheel={handleWheel}
          data-testid="quote-preview-scroll-area"
        >
          <iframe
            key={`${tpl}-${lang}-${html.length}`}
            title="teklif-preview"
            data-testid="quote-print-preview-frame"
            srcDoc={browserPrintHtml(html)}
            style={{
              width: 794,
              minHeight: 650,
              border: "none",
              borderRadius: 4,
              boxShadow: "0 2px 32px rgba(0,0,0,0.35)",
              background: "#ffffff",
              colorScheme: "light",
              transformOrigin: "top center",
              zoom: zoom,
              display: "block",
              flexShrink: 0,
            }}
          />
        </div>
        {showTailorSheet && <PrintPreviewModal order={quote} refType="quote" initialDocType="tulfon" onClose={() => setShowTailorSheet(false)} />}
        {showPrintCenter && <PrintPreviewModal order={quote} refType="quote" initialDocType="teklif" onClose={() => setShowPrintCenter(false)} />}
      </div>
    </div>
  );
}
